// src/pages/Clients/clientdetailsdrawer.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment-timezone';
import { stateTimezones } from '../../features/clients/clientsAPI';
import { deviceCommands, parseDeviceInfoResponse, getTargetDeviceId, isOLEDDevice } from '../../services/devicecommands';
import Swal from 'sweetalert2';
import { getMqttClient, waitForMqttConnection,checkDeviceOnline,subscribeToDevice,unsubscribeFromDevice } from '../../services/mqttClient';
import {
  Activity,
  AlertCircle,
  CalendarCheck,
  CalendarClock,
  Car,
  CheckCircle2,
  CircuitBoard,
  CircleGauge,
  Clock3,
  Cpu,
  Database,
  Flag,
  Fuel,
  Gauge,
  LoaderCircle,
  Mountain,
  Plus,
  Radio,
  Smartphone,
  Stethoscope,
  Thermometer,
  Wind,
  Wifi,
  SignalHigh,
  Wine,
  X
} from 'lucide-react';
import { buildCalibrationGroups, getCalibrationValue, A200U_DEVICE_INFO_GROUPS, formatDeviceInfoLabel, getDeviceInfoPresentation } from './calibrationDiagnostics';
import LastOperationProgress from './LastOperationProgress';
import DeviceSwapModal from './DeviceSwapModal';
import { formatDiagnosticParameter } from '../../utils/formatDiagnosticParameter';

// The device returns datebuf and timebuf as the same date/time value.  Keep
// timebuf out of the UI without removing its response position in the parser.
const HIDDEN_A200U_DEVICE_INFO_PARAMETERS = new Set(['timebuf']);

const buildDynamicDeviceInfoGroups = (data) => {
  const groups = new Map();

  Object.entries(data || {}).forEach(([parameter, value]) => {
    // Parser metadata is deliberately non-enumerable today, but this prevents
    // any future metadata from leaking into the customer-facing UI.
    if (parameter.startsWith('__')) return;

    const presentation = getDeviceInfoPresentation(parameter);
    if (!groups.has(presentation.group)) {
      groups.set(presentation.group, {
        title: presentation.group,
        icon: presentation.groupIcon,
        items: []
      });
    }

    groups.get(presentation.group).items.push({
      parameter,
      value: formatDiagnosticParameter(parameter, value),
      icon: presentation.icon
    });
  });

  return [...groups.values()];
};

// const A200U_DEVICE_INFO_PREVIEW_VALUES = [
//   'ABS_58_bf_25_a4_5f_44', '80', '09/04/2026 14:46:45',
//   '09/02/2026 07:57:37', '5', '',
//   'ABC_58_bf_25_a4_67_1c', '30', '837', '27', '0', '120', '95.410004',
//   '0', '0', '0', '0', '1', '1', '3', '20', '100', '7', '5', '7', '45',
//   '0', '300', '1800', '0', '0', '', '', '', '', '', '', '', '', '', 'A200U'
// ];

// const A200U_DEVICE_INFO_PREVIEW = Object.fromEntries(
//   A200U_DEVICE_INFO_GROUPS
//     .flatMap(group => group.parameters)
//     .map((parameter, index) => [parameter, A200U_DEVICE_INFO_PREVIEW_VALUES[index]])
// );

const OBD_DEFINITIONS = [
  ['Engine RPM', 'RPM', CircleGauge],
  ['Vehicle Speed', 'SPEED', CircleGauge],
  ['Coolant Temperature', 'COOLANT', Thermometer],
  ['Engine Load', 'ENGINE_LOAD', Activity],
  ['Throttle Position', 'THROTTLE', Gauge],
  ['Intake Manifold Pressure', 'MAP', Gauge],
  ['Fuel Level', 'FUEL', Fuel],
  ['Intake Air Temperature', 'INTAKE_TEMP', Wind],
  ['Timing Advance', 'TIMING', Clock3],
  ['Mass Air Flow (MAF) Rate', 'MAF', Wind, ['MAF_RATE']],
  ['Fuel Pressure', 'FUEL_PRESSURE', Gauge],
  ['Barometric Pressure', 'BARO', Gauge],
  ['Control Module Voltage (Battery)', 'BATTERY', Activity, ['VOLTAGE']],
  ['Ambient Air Temperature', 'AMBIENT', Thermometer],
  ['Engine Oil Temperature', 'ENGINE_OIL_TEMP', Thermometer],
  ['Oil Temperature', 'OIL', Thermometer],
  ['Engine Run Time', 'RUNTIME', Clock3],
  ['Distance Traveled with MIL ON', 'MIL_DIST', AlertCircle],
  ['Distance Since DTC Cleared', 'DIST_DTC_CLR', CheckCircle2],
  ['Run Time with MIL ON', 'RUNTIME_MIL', Clock3],
  ['Time Since DTC Cleared', 'TIME_DTC_CLR', Clock3],
  ['Commanded EGR', 'CMD_EGR', Activity, ['EGR']],
  ['Commanded EVAP Purge', 'EVAP', Activity],
  ['Relative Throttle Position', 'REL_THROTTLE', Gauge],
  ['Absolute Throttle Position B', 'ABS_THROTTLE_B', Gauge],
  ['Absolute Throttle Position C', 'ABS_THROTTLE_C', Gauge],
  ['Accelerator Pedal Position D', 'ACCEL_D', Gauge],
  ['Accelerator Pedal Position E', 'ACCEL_E', Gauge],
  ['Accelerator Pedal Position F', 'ACCEL_F', Gauge],
  ['Commanded Throttle Actuator', 'CMD_THROTTLE', Gauge],
  ['Odometer', 'ODOMETER', Car],
  ['Fuel Type', 'FUEL_TYPE', Fuel],
  ['Ethanol Fuel Percentage', 'ETHANOL', Fuel, ['ETHANOL_PERCENT']],
  ['Engine Fuel Rate', 'FUEL_RATE', Fuel]
].map(([parameter, obdKey, icon, aliases = []]) => ({
  parameter,
  obdKeys: [obdKey, ...aliases],
  icon
}));

const OBD_GROUPS = [
  {
    title: 'Engine Information',
    icon: Cpu,
    parameters: [
      'Engine RPM', 'Coolant Temperature', 'Engine Load',
      'Intake Air Temperature', 'Intake Manifold Pressure',
      'Engine Oil Temperature', 'Oil Temperature', 'Fuel Pressure',
      'Mass Air Flow (MAF) Rate', 'Engine Run Time'
    ]
  },
  {
    title: 'Vehicle Information',
    icon: Car,
    parameters: [
      'Vehicle Speed', 'Odometer', 'Distance Traveled with MIL ON',
      'Distance Since DTC Cleared'
    ]
  },
  {
    title: 'Fuel System',
    icon: Fuel,
    parameters: [
      'Fuel Level', 'Fuel Type', 'Ethanol Fuel Percentage', 'Engine Fuel Rate'
    ]
  },
  {
    title: 'Throttle & Pedal',
    icon: Gauge,
    parameters: [
      'Throttle Position', 'Relative Throttle Position',
      'Absolute Throttle Position B', 'Absolute Throttle Position C',
      'Accelerator Pedal Position D', 'Accelerator Pedal Position E',
      'Accelerator Pedal Position F', 'Commanded Throttle Actuator'
    ]
  },
  {
    title: 'Emission & Diagnostics',
    icon: Database,
    parameters: [
      'Timing Advance', 'Barometric Pressure',
      'Control Module Voltage (Battery)', 'Ambient Air Temperature',
      'Run Time with MIL ON', 'Time Since DTC Cleared',
      'Commanded EGR', 'Commanded EVAP Purge',
      'Diagnostic Trouble Codes (DTCs)'
    ]
  }
];

const unwrapValue = (value) => {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'String')) {
    return String(value.String || '').trim();
  }
  return value === null || value === undefined ? '' : String(value).trim();
};

const getPathValue = (source, path) =>
  path.split('.').reduce((value, key) => value?.[key], source);

const decodeObdPayload = (payload) => {
  let decoded = unwrapValue(payload);
  decoded = decoded
    .replace(/\\u\{([0-9a-fA-F]+)\}/g, (_, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&#([0-9]+);/g, (_, code) => String.fromCodePoint(parseInt(code, 10)));
  try {
    return decodeURIComponent(decoded);
  } catch {
    return decoded;
  }
};

const parseObdRecord = (record) => {
  const decoded = decodeObdPayload(record?.fullresp || record?.fullrep);
  const start = decoded.indexOf('&');
  if (start === -1) return null;

  const valueMap = decoded
    .slice(start + 1)
    .split(',')
    .reduce((map, part) => {
      const separator = part.indexOf('=');
      if (separator !== -1) {
        map[part.slice(0, separator).trim().toUpperCase()] =
          part.slice(separator + 1).trim();
      }
      return map;
    }, {});

  if (!Object.keys(valueMap).length) return null;

  const rows = OBD_DEFINITIONS.map(definition => {
    const matchingKey = definition.obdKeys.find(key => valueMap[key] !== undefined);
    const rawValue = matchingKey ? valueMap[matchingKey] : '';
    const value = !rawValue || ['NA', 'N/A', 'NULL', 'UNDEFINED'].includes(rawValue.toUpperCase())
      ? 'N/A'
      : rawValue;
    return { ...definition, value };
  });

  const dtcValues = ['DTC_STORED', 'DTC_PENDING', 'DTC_PERM']
    .map(key => {
      const value = valueMap[key];
      return value && !['NA', 'N/A'].includes(value.toUpperCase())
        ? `${key.replace('DTC_', '')}: ${value}`
        : null;
    })
    .filter(Boolean);
  rows.push({
    parameter: 'Diagnostic Trouble Codes (DTCs)',
    icon: AlertCircle,
    value: dtcValues.length ? dtcValues.join(', ') : 'N/A'
  });

  return { rows, updatedtime: record.updatedtime || 'N/A' };
};

const formatObdUpdatedTime = (value) => {
  if (!value || value === 'N/A') return 'N/A';
  const parsed = moment.utc(
    String(value).replace(/\(IST\)/i, '').trim(),
    ['DD/MM/YYYY HH:mm:ss', 'MM/DD/YYYY HH:mm:ss', moment.ISO_8601],
    true
  );
  return parsed.isValid() ? parsed.tz('Asia/Kolkata').format('DD/MM/YYYY HH:mm:ss') : value;
};

const EVENT_LOG_LABELS = {
  4: 'BYPASS', 6: 'SERVICE_PERIOD_SET', 7: 'VIOLATION_RESET',
  8: 'ONE_TIME_PASS_CODE', 10: 'CAR_BATTERY_OFF', 11: 'LOG_READ',
  14: 'SERVICE_ALERT', 17: 'INSUFFICIENTVOLUME_3', 18: 'TEST_REFUSED_RR_TEST',
  19: 'VIOLATION_LOCKOUT', 20: 'CAR_BATTERY_ON', 21: 'CAR_BATTERY_TAMPERING',
  22: 'CALIBRATION DONE', 24: 'TEST_REFUSED_RR_TEST_3', 25: 'HOT_SAMPLE',
  27: 'COOL_SAMPLE', 28: 'TAMPERED', 34: 'ENGINE_NOT_STARTED',
  60: 'BYPASS_START', 61: 'BYPASS_STOP', 62: 'SAMPLER_DISCONNECTED',
  65: 'INSUFFICIENTVOLUME', 66: 'HARD_BLOW', 100: 'Emergency_SOS',
  101: 'PICTURE_CAPTURED', 151: 'ALCOHOL_SENSOR_FAIL',
  152: 'BREATH_PRESSURE_SEN_FAIL', 153: 'BREATH_TEMP_SEN_FAIL',
  154: 'BMP180_ALTITUDE_FAIL', 155: 'PROXIMITY_SENSOR_FAIL',
  156: 'TMP75_AMB_TEMP_SENS_FAIL', 157: 'RTC_FAIL', 217: 'MEMSET',
  218: 'DEVICE RESTART', 219: 'TIME_SET', 220: 'LOCKOUT 0',
  221: 'LOCKOUT 1', 222: 'LOCKOUT 2', 223: 'LOCKOUT 3',
  224: 'LOCKOUT 4', 225: 'LOCKOUT 5', 226: 'LOCKOUT 6', 227: 'LOCKOUT 7',111 : 'Handset Changed',1:'Powered_on',41: 'OTP 1',
42: 'OTP 2',
43: 'OTP 3',
44: 'OTP 4',
};

const formatEventLog = (log, deviceMode, stateTimezone) => {
  const eventCode = String(log?.typeofevent ?? log?.event ?? '').trim();
  const isTest = eventCode === '254';
  let event = '';

if (isTest) {
  event =
    Number(log?.randominitial) === 2 ||
    Number(log?.randominitial) === 0
      ? 'INITIAL TEST'
      : Number(log?.randominitial) === 3 ||
        Number(log?.randominitial) === 1
      ? 'RANDOM TEST'
      : 'NA';
}

else if (eventCode === '15') {
  event =
    Number(log?.enginerunstate) === 0
      ? 'ENGINE OFF'
      : Number(log?.enginerunstate) === 1
      ? 'ENGINE ON'
      : 'NA';
}

else if (
  eventCode === '41' ||
  eventCode === '42' ||
  eventCode === '43' ||
  eventCode === '44'
) {
  const position = Number(eventCode) - 40;

  event = `OTP_${log?.bacvalue || ''}_${position}`;
}

else {
  event = EVENT_LOG_LABELS[eventCode] || eventCode;
}
  // const event = isTest
  //   ? (Number(log?.randominitial) === 2 || Number(log?.randominitial) === 0 ? 'INITIAL TEST'
  //     : Number(log?.randominitial) === 3 || Number(log?.randominitial) === 1 ? 'RANDOM TEST' : 'NA')
  //   : eventCode === '15'
  //     ? (Number(log?.enginerunstate) === 0 ? 'ENGINE OFF' : Number(log?.enginerunstate) === 1 ? 'ENGINE ON' : 'NA')
  //     : EVENT_LOG_LABELS[eventCode] || eventCode;
  const rawTime = log?.rtime || log?.time || log?.updatedtime || '';
  const parsed = moment.utc(rawTime, ['MM/DD/YYYY HH:mm:ss', 'M/D/YYYY H:mm', 'M/D/YYYY H:mm:ss', moment.ISO_8601], true);
  const time = String(deviceMode || '').trim().toUpperCase() === 'A300I' || !stateTimezone || !parsed.isValid()
    ? rawTime
    : parsed.tz(stateTimezone).format('MM/DD/YYYY HH:mm:ss');
  const bacNumber = Number(log?.bacvalue);
  const bac =
  ['41', '42', '43', '44'].includes(eventCode)
    ? ''
    : log?.bacvalue !== undefined &&
      log?.bacvalue !== null &&
      bacNumber !== 255 &&
      Number.isFinite(bacNumber)
    ? (bacNumber / 1000).toFixed(3)
    : '';
  // const bac = log?.bacvalue !== undefined && log?.bacvalue !== null && bacNumber !== 255 && Number.isFinite(bacNumber)
  //   ? (bacNumber / 1000).toFixed(3)
  //   : '';
  const result = isTest
    ? (Number(log?.passfailstatus) === 1 ? 'PASS' : Number(log?.passfailstatus) === 0 ? 'FAIL' : '')
    : (log?.result || log?.passfailstatus || '');

  return {
    samplerId: log?.devid || log?.devId || log?.samplerId || '',
    controlId: log?.controlId || log?.cbid || '',
    time,
    event,
    bac,
    result,
      rawEventType: eventCode
  };
};

// const CommandNetworkIcons = () => (
//   <span className="command-network-icons" aria-label="Available via Wi-Fi or 4G">
//    <Wifi
//   size={15}
//   className={
//     mqttDeviceOnline
//       ? 'wifi-online'
//       : 'wifi-offline'
//   }
// />
//     {/* <Wifi size={15} aria-hidden="true" /> /  */}
//     <SignalHigh size={15} aria-hidden="true" />
//   </span>
// );

const CommandNetworkIcons = ({
  wifiOnline = false,
}) => (
  <span
    className="command-network-icons"
    aria-label="Available via Wi-Fi or 4G"
  >
    <Wifi
      size={15}
      className={
        wifiOnline
          ? 'wifi-online'
          : 'wifi-offline'
      }
      aria-hidden="true"
    />

    <SignalHigh
      size={15}
      aria-hidden="true"
    />
  </span>
);

const ClientDetailsDrawer = ({ isOpen, onClose, client, user, oledDevices, onAddToRemovals, onSwapSuccess }) => {
  const [showSwap, setShowSwap] = useState(false);
  useEffect(() => { setShowSwap(false); }, [isOpen, client?.id]);
  const [mqttStatus, setMqttStatus] = useState('checking');
  const [mqttAttempt, setMqttAttempt] = useState(0);
  const mqttDeviceId = client?.cbid || client?.devId;
  const [mqttDeviceOnline, setMqttDeviceOnline] =
  useState(false);

  const serialNumber = String(
  client?.sno || ''
).toUpperCase();

const shouldCheckWifi =
  serialNumber.includes('CAV4') ||
  serialNumber.includes('INDIAV4') ||
  serialNumber.includes('TXV4');

const [mqttDeviceChecking, setMqttDeviceChecking] =
  useState(false);
 useEffect(() => {
  if (
    !isOpen ||
    !mqttDeviceId ||
    !shouldCheckWifi
  ) {
    setMqttDeviceOnline(false);

    console.log(
      'WiFi MQTT check skipped:',
      client?.sno
    );

    return;
  }

  const controller = new AbortController();
  const connection = getMqttClient();

  let wifiCheckInterval = null;
  let isCheckingWifi = false;

  const checkDevice = async () => {
    // Prevent overlapping 999 checks
    if (isCheckingWifi) {
      return;
    }

    isCheckingWifi = true;

    try {
      if (!connection.connected) {
        console.log(
          '❌ Browser MQTT not connected'
        );

        setMqttStatus('disconnected');
        setMqttDeviceOnline(false);

        return;
      }

      console.log(
        '🔎 Checking WiFi using CMD 999:',
        mqttDeviceId
      );

      const online =
        await checkDeviceOnline(
          mqttDeviceId
        );

      if (controller.signal.aborted) {
        return;
      }

      setMqttDeviceOnline(
        online
      );

      if (online) {
        console.log(
          '🟢 WIFI ONLINE'
        );
      } else {
        console.log(
          '🔴 WIFI OFFLINE'
        );
      }

    } catch (error) {
      if (
        !controller.signal.aborted
      ) {
        console.log(
          '❌ WiFi check error:',
          error.message
        );

        setMqttDeviceOnline(
          false
        );
      }
    } finally {
      isCheckingWifi = false;
    }
  };

  const startWifiChecking =
    async () => {
      try {
        setMqttStatus(
          'checking'
        );

        setMqttDeviceOnline(
          false
        );

        // Wait until browser connects
        // to MQTT broker
        await waitForMqttConnection(
          controller.signal
        );

        if (
          controller.signal.aborted
        ) {
          return;
        }

        setMqttStatus(
          'connected'
        );

        // Subscribe before first 999
        await subscribeToDevice(
          mqttDeviceId
        );

        if (
          controller.signal.aborted
        ) {
          return;
        }

        console.log(
          '✅ Subscribed:',
          `device/${mqttDeviceId}/cmdack`
        );

        // Check immediately
        await checkDevice();

        if (
          controller.signal.aborted
        ) {
          return;
        }

        // Then check every 5 seconds
        wifiCheckInterval =
          setInterval(() => {
            checkDevice();
          }, 30000);

      } catch (error) {
        if (
          controller.signal.aborted
        ) {
          return;
        }

        console.log(
          '❌ MQTT setup failed:',
          error.message
        );

        setMqttStatus(
          'failed'
        );

        setMqttDeviceOnline(
          false
        );
      }
    };

  const onConnect = () => {
    console.log(
      '✅ MQTT broker connected'
    );

    setMqttStatus(
      'connected'
    );

    // Check immediately again
    checkDevice();
  };

  const onDisconnect = () => {
    console.log(
      '❌ MQTT broker disconnected'
    );

    setMqttStatus(
      'disconnected'
    );

    setMqttDeviceOnline(
      false
    );
  };

  connection.on(
    'connect',
    onConnect
  );

  connection.on(
    'offline',
    onDisconnect
  );

  connection.on(
    'close',
    onDisconnect
  );

  startWifiChecking();

  return () => {
  controller.abort();

  if (wifiCheckInterval) {
    clearInterval(
      wifiCheckInterval
    );
  }

  connection.removeListener(
    'connect',
    onConnect
  );

  connection.removeListener(
    'offline',
    onDisconnect
  );

  connection.removeListener(
    'close',
    onDisconnect
  );

  // Unsubscribe from this CBID
  unsubscribeFromDevice(
    mqttDeviceId
  ).catch(error => {
    console.log(
      'MQTT unsubscribe cleanup error:',
      error.message
    );
  });

  setMqttDeviceOnline(
    false
  );
};

}, [
  isOpen,
  mqttDeviceId,
  mqttAttempt,
]);
useEffect(() => {
  // Only for V4 devices
  if (!shouldCheckWifi) {
    return;
  }

  const handleBeforeUnload = () => {
    if (!mqttDeviceId) {
      return;
    }

    const connection =
      getMqttClient();

    const topic =
      `device/${mqttDeviceId}/cmdack`;

    if (connection.connected) {
      console.log(
        '🔌 Browser reload/close → unsubscribe:',
        topic
      );

      connection.unsubscribe(
        topic
      );
    }
  };

  window.addEventListener(
    'beforeunload',
    handleBeforeUnload
  );

  return () => {
    window.removeEventListener(
      'beforeunload',
      handleBeforeUnload
    );
  };
}, [
  mqttDeviceId,
  shouldCheckWifi,
]);

  const [postCommandOpen, setPostCommandOpen] = useState(false);
  const [postCommandSaving, setPostCommandSaving] = useState(false);
  const [postCommandView, setPostCommandView] = useState('list');
  const [postCommands, setPostCommands] = useState([]);
  const [postCommandsLoading, setPostCommandsLoading] = useState(false);
  const [postCommandsError, setPostCommandsError] = useState('');
  const [postCommandsPage, setPostCommandsPage] = useState(1);
  const [postCommandForm, setPostCommandForm] = useState({ command: '', operationDate: '', graceTime: '', status: 'Active' });
  const [editingCommandId, setEditingCommandId] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({
    samplerId: 'Loading...',
    controlBoardId: 'Loading...',
    firmware: 'Loading...',
    cbVersion: 'Loading...',
    s3Version: '',
    calibrationDate: 'Loading...',
    lockoutDate: 'Loading...',
    lastUpdate: 'Loading...'
  });
  
  const [connectionStatus, setConnectionStatus] = useState({
    cellular: 'checking',
    wifi: 'checking',
    cellularLastUpdate: null,
    wifiLastUpdate: null,
    hasWifi: true
  });
  
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [firmwareVersion, setFirmwareVersion] = useState(0);
  const [otps, setOtps] = useState([]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpNotice, setOtpNotice] = useState('');
  const [isDataLoading, setIsDataLoading] = useState(true);
  const otpRequestRef = useRef(0);
  useEffect(() => {
    // The separate alert renders even while device information is loading.
    if (!otpNotice || !isOpen || otpLoading) return;
    const timer = setTimeout(() => setOtpNotice(''), 3000);
    return () => clearTimeout(timer);
  }, [otpNotice, isOpen, otpLoading]);

  const postCommandOptions = [
    ['84', 'Take Test'], ['90', 'Lock Device'], ['91', 'Unlock Device'],
    ['83', 'Take Picture'], ['85', 'Violation Reset'], ['82', 'Restart Device'],
    ['104', 'Set OTP'], ['94', 'Device Config'], ['80', 'Device Info'],
    ['88', 'Bypass 5'], ['89', 'UnBypass 5']
  ];
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [diagnosticsTab, setDiagnosticsTab] = useState('calibration');
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [calibrationData, setCalibrationData] = useState(null);
  const [obdData, setObdData] = useState(null);
  const [deviceInfoModalOpen, setDeviceInfoModalOpen] = useState(false);
  const [deviceInfoCommandData, setDeviceInfoCommandData] = useState(null);
  const [deviceInfoCommandMode, setDeviceInfoCommandMode] = useState('');
  
  let pingIntervalRef = useRef(null);
  let isMountedRef = useRef(true);
  const timezoneCacheRef = useRef({});
  const diagnosticsRequestRef = useRef(0);
  const deviceInfoRequestRef = useRef(0);
  
  const positions = ['0', '1', '2', '3', '4'];

  // Reset all states when client changes
  const resetStates = useCallback(() => {
    setDeviceInfo({
      samplerId: 'Loading...',
      controlBoardId: 'Loading...',
      firmware: 'Loading...',
      cbVersion: 'Loading...',
      s3Version: 'N/A',
      calibrationDate: 'Loading...',
      lockoutDate: 'Loading...',
      lastUpdate: 'Loading...'
    });
    setConnectionStatus({
      cellular: 'checking',
      wifi: 'checking',
      cellularLastUpdate: null,
      wifiLastUpdate: null,
      hasWifi: true
    });
    setOtps([]);
    setOtpError('');
    setOtpNotice('');
    setFirmwareVersion(0);
    setIsDataLoading(true);
    diagnosticsRequestRef.current += 1;
    setDiagnosticsOpen(false);
    setDiagnosticsTab('calibration');
    setDiagnosticsLoading(false);
    setCalibrationData(null);
    setObdData(null);
    setDeviceInfoModalOpen(false);
    setDeviceInfoCommandData(null);
    setDeviceInfoCommandMode('');
  }, []);

  // Fetch OTPs from device
  const fetchOTPs = async () => {
    if (!client || !isMountedRef.current) return;
    const request = ++otpRequestRef.current;

    setOtpLoading(true);
    setOtpError('');
    setOtpNotice('');
    try {
      const isOled = isOLEDDevice(client.sno, oledDevices);
      const deviceIds = [...new Set(
        (isOled ? [client.devId, client.cbid] : [client.cbid, client.devId])
          .filter(Boolean)
          .map(value => String(value).trim())
          .filter(Boolean)
      )];

      if (!deviceIds.length) throw new Error('No device ID is available');

      let data = [];
      for (const deviceId of deviceIds) {
        const response = await fetch(
          `https://alcobrakeadmin.com/v3/v2/ping/getdeviceotps?deviceId=${encodeURIComponent(deviceId)}`
        );
        if (!response.ok) continue;

        const payload = await response.json();
        const records = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.otps)
              ? payload.otps
              : [];
        if (records.length) {
          data = records;
          break;
        }
      }

      if (isMountedRef.current && request === otpRequestRef.current) {
        setOtps(data);
        const hasAvailableEmergencyOTP = data.some(item =>
          ['0', '1', '2', '3'].includes(String(item.position ?? item.pos ?? '')) &&
          String(item.status ?? '').trim().toUpperCase() === 'ACTIVE'
        );
        if (!hasAvailableEmergencyOTP) {
          setOtpNotice('No emergency OTPs available. Please use Set OTP to set the OTP.');
        }
      }
    } catch (error) {
      console.error('Failed to fetch OTPs:', error);
      if (isMountedRef.current && request === otpRequestRef.current) {
        setOtps([]);
        setOtpNotice('Unable to load emergency OTPs. Please try again.');
      }
    } finally {
      if (isMountedRef.current && request === otpRequestRef.current) {
        setOtpLoading(false);
      }
    }
  };
  
  // Get OTP for a specific position
  const getOTPForPosition = (position) => {
    const otp = otps.find(item => String(item.position ?? item.pos ?? '') === String(position));
    return otp ? (otp.otp ?? otp.code ?? 'XXXX') : 'XXXX';
  };
  
  const getOTPStatus = (position) => {
    const otp = otps.find(item => String(item.position ?? item.pos ?? '') === String(position));
    const status = String(otp?.status ?? '').trim().toUpperCase();
    if (status === 'ACTIVE') return { className: 'otp-available', label: 'Available' };
    if (status === 'USED') return { className: 'otp-used', label: 'Used' };
    return { className: 'otp-unavailable', label: otp ? 'Unknown status' : 'Not available' };
  };
  
  // ✅ Format firmware version for display - show exactly what API returns
  const formatFirmwareVersion = (version) => {
    if (version === 'Loading...') return 'Loading...';
    if (!version) return 'N/A';
    if (version === 'N/A') return 'N/A';
    if (version === 'Error loading') return 'Error loading';
    
    // Return exactly what the API returns
    return String(version);
  };
  
  // ✅ Get numeric firmware version for command availability comparison
  const getNumericFirmware = (version) => {
    if (!version || version === 'Loading...') return 0;
    if (version === 'N/A') return 0;
    if (version === 'Error loading') return 0;
    const versionStr = String(version).trim();
    // Parse the version as a number
    // This correctly handles:
    // - "25" → 25
    // - "42" → 42  
    // - "50" → 50
    // - "0.94" → 0.94
    // - "0.930000" → 0.93
    const num = parseFloat(versionStr);
    if (isNaN(num)) return 0;
    return num;
  };
 
  // Device Information is populated from the device APIs, not from the client-list
  // snapshot. OLED/sampler devices use their devId endpoints; all other devices use CBID.
  const fetchDeviceInfo = useCallback(async () => {
    if (!client || !isMountedRef.current) return;
    const requestId = ++deviceInfoRequestRef.current;
    const isCurrentRequest = () => isMountedRef.current && requestId === deviceInfoRequestRef.current;
 
    const deviceCbid = client.cbid || '';
    const handsetId = client.devId || '';
    const isOledDevice = isOLEDDevice(client.sno, oledDevices);
    const trackingUrl = isOledDevice
      ? `https://alcobrakeadmin.com/v3/v2/caltrack/getdevcaltracks?devId=${encodeURIComponent(handsetId)}`
      : `https://alcobrakeadmin.com/v3/v2/caltrack/getdevcaltracks2?cbid=${encodeURIComponent(deviceCbid)}`;
    const logsUrl = isOledDevice
      ? `https://robopower.xyz/v2/eeprom/getalllogs?deviceId=${encodeURIComponent(handsetId)}`
      : `https://robopower.xyz/v2/eeprom/getalllogs2?cbid=${encodeURIComponent(deviceCbid)}`;
    const timezoneUrl = `https://robopower.xyz/us/cadevices/getuniquedevice?sno=${encodeURIComponent(client.sno || '')}`;
    const baseInfo = {
      samplerId: deviceCbid || 'N/A',
      controlBoardId: handsetId || 'N/A',
      firmware: 'N/A',
      cbVersion: 'N/A',
      s3Version: '',
      calibrationDate: 'N/A',
      lockoutDate: 'N/A',
      lastUpdate: 'NO UPDATE'
    };
 
    if ((isOledDevice && !handsetId) || (!isOledDevice && !deviceCbid)) {
      console.warn('[Calibration Dates]', {
        sno: client.sno, logsUrl,
        reason: 'Missing required device identifier; API request skipped',
        calibrationDate: 'N/A', lockoutDate: 'N/A'
      });
      console.warn('[Device Information] Missing required device identifier', {
        deviceType: isOledDevice ? 'OLED/sampler' : 'normal CBID',
        cbid: deviceCbid,
        devId: handsetId,
        sno: client.sno
      });
      if (isMountedRef.current) {
        setDeviceInfo(baseInfo);
        setFirmwareVersion(0);
        setIsDataLoading(false);
      }
      return;
    }
 
    let requestTimeout;
    let versionInfo = {};
    try {
      console.groupCollapsed('[Device Information] Loading device data');
      console.log('Selected client input', {
        cbid: client.cbid,
        devId: client.devId,
        sno: client.sno,
        tagstate: client.tagstate,
        calibrationperiod: client.calibrationperiod,
        deviceType: isOledDevice ? 'OLED/sampler' : 'normal CBID'
      });
      console.log('API URLs', { trackingUrl, logsUrl, timezoneUrl });
      const controller = new AbortController();
      requestTimeout = setTimeout(() => controller.abort(), 8000);
      const [trackingResult, logsResult, timezoneResult] = await Promise.allSettled([
        fetch(trackingUrl, { signal: controller.signal })
          .then(response => {
            if (!response.ok) throw new Error(`Tracking API returned ${response.status}`);
            return response.json();
          })
          .then(payload => {
            // Publish versions independently: log processing must not erase them.
            const groups = Array.isArray(payload) ? payload : payload?.data;
            const records = (Array.isArray(groups) ? groups : []).flatMap(group => {
              if (!group || typeof group !== 'object') return [];
              if (!Array.isArray(group.records)) return [group];
              const { records: nestedRecords, ...defaults } = group;
              return nestedRecords.filter(record => record && typeof record === 'object')
                .map(record => ({ ...defaults, ...record }));
            }).sort((a, b) => {
              const timestamp = record => {
                const raw = record.updatedtime?.String ?? record.updatedtime ?? '';
                return moment(String(raw).trim().replace(/\s*\([A-Za-z]{2,5}\)\s*$/, ''),
                  ['MM/DD/YYYY HH:mm:ss', 'M/D/YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true).valueOf();
              };
              const difference = timestamp(b) - timestamp(a);
              return Number.isFinite(difference) && difference !== 0 ? difference : Number(b.id || 0) - Number(a.id || 0);
            });
            const resolveVersion = fields => {
              for (const record of records) {
                for (const field of fields) {
                  const raw = record[field];
                  const value = raw && typeof raw === 'object'
                    ? (raw.Valid === false || raw.valid === false ? '' : raw.String ?? raw.string ?? raw.Float64 ?? raw.Int64 ?? '')
                    : raw ?? '';
                  const text = String(value).trim();
                  if (text && !['n/a', 'null', 'undefined'].includes(text.toLowerCase())) return text;
                }
              }
              return 'N/A';
            };
            versionInfo = {
              firmware: resolveVersion(['fwversion', 'firmwareVersion', 'fversion']),
              cbVersion: resolveVersion(['cbversion', 'cbVersion', 'cb_version']),
              s3Version: resolveVersion(['s3version', 's3Version', 's3_version'])
            };
            if (isCurrentRequest()) {
              setDeviceInfo(previous => ({ ...previous, ...versionInfo }));
              setFirmwareVersion(getNumericFirmware(versionInfo.firmware));
            }
            return payload;
          }),
        fetch(logsUrl, { signal: controller.signal })
          .then(response => {
            if (!response.ok) throw new Error(`Logs API returned ${response.status}`);
            return response.json();
          }),
        client.sno
          ? fetch(timezoneUrl, { signal: controller.signal }).then(response => response.ok ? response.json() : [])
          : Promise.resolve([])
      ]);
      clearTimeout(requestTimeout);
      requestTimeout = null;
      const trackingPayload = trackingResult.status === 'fulfilled' ? trackingResult.value : [];
      if (trackingResult.status === 'rejected') {
        console.error('[Device Versions] Tracking request failed', trackingResult.reason);
      }
      const logsPayload = logsResult.status === 'fulfilled' ? logsResult.value : [];
      console.log('[Calibration Dates] Logs API', {
        sno: client.sno,
        url: logsUrl,
        status: logsResult.status,
        error: logsResult.status === 'rejected' ? String(logsResult.reason) : null,
        response: logsPayload
      });
      const timezonePayload = timezoneResult.status === 'fulfilled' ? timezoneResult.value : [];
      const trackingGroups = Array.isArray(trackingPayload) ? trackingPayload : trackingPayload?.data || [];
      // CBID tracking responses group rows by calibration date. Versions live
      // inside records; older device endpoints can still return flat rows.
      const trackingRecords = (Array.isArray(trackingGroups) ? trackingGroups : []).flatMap(group => {
        if (!group || typeof group !== 'object') return [];
        if (!Array.isArray(group.records)) return [group];
        return group.records
          .filter(record => record && typeof record === 'object')
          .map(record => ({ ...group, ...record, caldate: record.caldate || group.caldate }));
      });
      const logRows = Array.isArray(logsPayload) ? logsPayload : logsPayload?.data;
      const logs = (Array.isArray(logRows) ? logRows : [])
        .filter(log => log && typeof log === 'object');
      const readApiValue = (value) => {
        if (value && typeof value === 'object') {
          if (value.Valid === false || value.valid === false) return '';
          return value.String ?? value.string ?? value.Float64 ?? value.Int64 ?? '';
        }
        return value ?? '';
      };
      const timezone = String(readApiValue(timezonePayload?.[0]?.timezone) || '').trim();
      const shouldConvertFromGmt = timezone.toUpperCase() === 'GMT';
      const stateTimezones = {
        INDIA: 'Asia/Kolkata', IN: 'Asia/Kolkata',
        CA: 'America/Los_Angeles', CALIFORNIA: 'America/Los_Angeles',
        TX: 'America/Chicago', TEXAS: 'America/Chicago',
        NY: 'America/New_York', FL: 'America/New_York',
        IL: 'America/Chicago',
        AZ: 'America/Phoenix', CO: 'America/Denver',
        WA: 'America/Los_Angeles', OR: 'America/Los_Angeles'
      };
      const targetTimezone = stateTimezones[String(client.tagstate || '').trim().toUpperCase()] || 'UTC';
      const parseDeviceTime = (value, isGmt = shouldConvertFromGmt) => {
        if (!value) return null;
        // Tracking timestamps can include a suffix such as " (IST)". Remove
        // it before strict parsing so latest records are actually sortable.
        const raw = String(value).trim().replace(/\s*\([A-Za-z]{2,5}\)\s*$/, '');
        const parsed = isGmt
          ? moment.utc(raw, ['MM/DD/YYYY HH:mm:ss', 'M/D/YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true)
          : moment(raw, ['MM/DD/YYYY HH:mm:ss', 'M/D/YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
        return parsed.isValid() ? parsed : null;
      };
      const displayLogTime = (value) => {
        if (!value) return 'N/A';
        if (!shouldConvertFromGmt) return String(value);
        const parsed = parseDeviceTime(value, true);
        return parsed ? parsed.tz(targetTimezone).format('MM/DD/YYYY HH:mm:ss') : String(value);
      };
      const sortedTrackingRecords = [...trackingRecords].sort((a, b) => {
        const bTime = parseDeviceTime(readApiValue(b.updatedtime), false)?.valueOf();
        const aTime = parseDeviceTime(readApiValue(a.updatedtime), false)?.valueOf();
        // IDs increase with new tracking records. Use them only when a
        // timestamp is missing or malformed, preserving reliable ordering.
        if (bTime != null && aTime != null && bTime !== aTime) return bTime - aTime;
        return Number(b.id || 0) - Number(a.id || 0);
      });
      const latestCal = sortedTrackingRecords[0];
      
      const calibrationLog = [...logs]
        .filter(log => {
          const event = String(readApiValue(log.typeofevent || log.typeOfEvent || log.event) || '').trim().toUpperCase();
          return event === '22' || event === 'CALIBRATION DONE';
        })
        .sort((a, b) => (parseDeviceTime(readApiValue(b.rtime))?.valueOf() || 0) - (parseDeviceTime(readApiValue(a.rtime))?.valueOf() || 0))[0];
      const sortedLogs = [...logs].sort((a, b) =>
        (parseDeviceTime(readApiValue(b.rtime))?.valueOf() || 0) - (parseDeviceTime(readApiValue(a.rtime))?.valueOf() || 0)
      );
      const calibrationRawTime = readApiValue(calibrationLog?.rtime);
      const calibrationDate = displayLogTime(calibrationRawTime);
      const calibrationMoment = parseDeviceTime(calibrationRawTime);
      const lockoutDays = String(client.tagstate || '').trim().toUpperCase() === 'TX' ? 37 : 67;
      const lockoutDate = calibrationMoment?.isValid()
        ? (shouldConvertFromGmt ? calibrationMoment.tz(targetTimezone) : calibrationMoment).clone().add(lockoutDays, 'days').format('MM/DD/YYYY HH:mm:ss')
        : 'N/A';
      console.log('[Calibration Dates] Result', {
        sno: client.sno,
        url: logsUrl,
        logCount: logs.length,
        calibrationEvent: calibrationLog || null,
        rawTime: calibrationRawTime,
        validTime: Boolean(calibrationMoment?.isValid()),
        tagstate: client.tagstate,
        timezoneFromApi: timezone,
        targetTimezone: shouldConvertFromGmt ? targetTimezone : 'No conversion',
        lockoutDays,
        calibrationDate,
        lockoutDate,
        reason: logsResult.status === 'rejected' ? 'Logs API failed or timed out'
          : !logs.length ? 'No logs returned'
          : !calibrationLog ? 'No event 22 / CALIBRATION DONE found'
          : !calibrationRawTime ? 'Calibration event has no rtime'
          : !calibrationMoment?.isValid() ? 'Calibration rtime cannot be parsed; lockout date unavailable'
          : 'Dates calculated successfully'
      });
      const firmware = versionInfo.firmware || 'N/A';
      const resolvedInfo = {
        ...baseInfo,
        firmware,
        cbVersion: versionInfo.cbVersion || 'N/A',
        s3Version: versionInfo.s3Version || 'N/A',
        calibrationDate,
        lockoutDate,
        lastUpdate: sortedLogs.length ? displayLogTime(readApiValue(sortedLogs[0]?.rtime)) : 'NO UPDATE'
      };
      console.log('Resolved Device Information', {
        timezoneFromApi: timezone || '(blank)',
        timezoneRule: shouldConvertFromGmt ? `GMT converted to ${targetTimezone}` : 'backend timestamps displayed without conversion',
        latestTrackingRecord: latestCal,
        latestCalibrationLog: calibrationLog,
        latestLog: sortedLogs[0],
        lockoutDays,
        displayValues: resolvedInfo
      });
      console.groupEnd();
      console.log('[Device Versions]', {
        samplerBoardVersion: resolvedInfo.firmware,
        controlBoardVersion: resolvedInfo.cbVersion,
        s3Version: resolvedInfo.s3Version,
        trackingRecords: sortedTrackingRecords.length
      });
 
      if (isCurrentRequest()) {
        setDeviceInfo(resolvedInfo);
        setFirmwareVersion(getNumericFirmware(firmware));
        setIsDataLoading(false);
      }
    } catch (error) {
      if (requestTimeout) clearTimeout(requestTimeout);
      console.error('Error fetching device info:', error);
      console.error('[Calibration Dates] Loading failed', {
        sno: client.sno, url: logsUrl, error: String(error),
        calibrationDate: 'N/A', lockoutDate: 'N/A'
      });
      console.groupEnd();
      if (isCurrentRequest()) {
        setDeviceInfo({ ...baseInfo, ...versionInfo });
        setIsDataLoading(false);
      }
    }
  }, [client, oledDevices]);
  
const fetchConnectionStatus = useCallback(async () => {
  console.log('Connection status check started');

  if (!client || !client.cbid || !isMountedRef.current) {
    console.log('Connection status check skipped');
    return;
  }

  try {
    const devid = client.cbid;

    console.log('CBID:', devid);

    const response = await fetch(
      `https://alcobrakeadmin.com/v3/v2/ping/latestnetworkblinking?cbid=${encodeURIComponent(
        devid
      )}`
    );

    const data = await response.json();

    if (!isMountedRef.current) return;

    const lastUpdate4G = data?.['4g_gtime'];

    const currentTimeGMT =
      client.devicemode === 'A300I'
        ? moment().format('YYYY-MM-DD HH:mm:ss')
        : moment().tz('GMT').format('YYYY-MM-DD HH:mm:ss');

    let cellularStatus = 'off';

    // ---------------------------------------------
    // 4G blinking only
    // ---------------------------------------------
    if (lastUpdate4G) {
      const diff4G = moment(currentTimeGMT).diff(
        moment(lastUpdate4G),
        'seconds'
      );

      console.log(
        '4G difference:',
        diff4G,
        'seconds'
      );

      if (diff4G >= 0 && diff4G < 30) {
        cellularStatus = 'on';
      }
    }

    // ---------------------------------------------
    // WiFi comes ONLY from MQTT cmd 999 ACK
    // ---------------------------------------------
    const wifiStatus =
      mqttDeviceOnline ? 'on' : 'off';

    console.log(
      'WiFi MQTT status:',
      wifiStatus
    );

    if (isMountedRef.current) {
      const newStatus = {
        cellular: cellularStatus,
        wifi: wifiStatus,
        cellularLastUpdate: lastUpdate4G,
        wifiLastUpdate: null,
        hasWifi: true,
      };

      console.log(
        'FINAL CONNECTION STATUS:',
        newStatus
      );

      setConnectionStatus(newStatus);
    }

  } catch (error) {
    console.error(
      'Connection status error:',
      error
    );
  }
}, [client, mqttDeviceOnline]);  
  const startConnectionPolling = useCallback(() => {
    if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    fetchConnectionStatus();
    pingIntervalRef.current = setInterval(fetchConnectionStatus, 30000);
  }, [fetchConnectionStatus]);
  
  // Effect for when client changes
  useEffect(() => {
    isMountedRef.current = true;
    
    if (isOpen && client) {
      // Reset all states first to show loading indicators
      resetStates();
      
      // Then fetch new data
      fetchDeviceInfo();
      startConnectionPolling();
      fetchOTPs();
    }
    
    return () => {
      isMountedRef.current = false;
      otpRequestRef.current += 1;
      deviceInfoRequestRef.current += 1;
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [isOpen, client, fetchDeviceInfo, startConnectionPolling, resetStates]);
  
  const showAlert = (title, message, isSuccess = true) => {
    Swal.fire({
      title,
      text: message,
      icon: isSuccess ? 'success' : 'error',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const closePostCommand = () => {
    if (postCommandSaving) return;
    setPostCommandOpen(false);
    setPostCommandView('list');
    setPostCommandForm({ command: '', operationDate: '', graceTime: '', status: 'Active' });
  };

  const getQueueTimezone = async cbid => {
    if (!cbid) return 'UTC';
    if (timezoneCacheRef.current[cbid]) return timezoneCacheRef.current[cbid];
    try {
      const response = await fetch(`https://alcobrakeadmin.com/v3/app/v2data/getlatestpingv22?cbid=${encodeURIComponent(cbid)}`);
      if (!response.ok) throw new Error('Location request failed');
      const data = await response.json();
      const latitude = Number(data?.lat);
      const longitude = Number(data?.lon);
      let timezone = 'UTC';
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        if (latitude >= 6 && latitude <= 38 && longitude >= 68 && longitude <= 98) timezone = 'Asia/Kolkata';
        else if (latitude >= 18 && latitude <= 23 && longitude >= -161 && longitude <= -154) timezone = 'Pacific/Honolulu';
        else if (latitude >= 51 && latitude <= 72 && longitude >= -170 && longitude <= -129) timezone = 'America/Anchorage';
        else if (latitude >= 24 && latitude <= 50 && longitude >= -125 && longitude <= -66) {
          timezone = longitude <= -114 ? 'America/Los_Angeles' : longitude <= -101 ? 'America/Denver' : longitude <= -85 ? 'America/Chicago' : 'America/New_York';
        }
      }
      timezoneCacheRef.current[cbid] = timezone;
      return timezone;
    } catch (error) {
      timezoneCacheRef.current[cbid] = 'UTC';
      return 'UTC';
    }
  };

  const formatQueueTime = (date, timezone) => {
    if (!date) return '';
    const parsed = moment.utc(date, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DDTHH:mm:ss.SSSZ'], true);
    return (parsed.isValid() ? parsed : moment.utc(date)).tz(timezone || 'UTC').format('YYYY-MM-DD HH:mm:ss');
  };

  const fetchPostCommands = async () => {
    if (!client?.id) return;
    setPostCommandsLoading(true);
    setPostCommandsError('');
    try {
      await getQueueTimezone(client.cbid);
      const response = await fetch(`https://alcobrakeadmin.com/v3/app/queuecommands/getclientcommands?clientId=${encodeURIComponent(client.id)}`);
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      const data = await response.json();
      const commands = await Promise.all((Array.isArray(data) ? data : []).map(async item => {
        const timezone = await getQueueTimezone(item.deviceid);
        return {
          ...item,
          operationdate: formatQueueTime(item.operationdate, timezone),
          createddate: formatQueueTime(item.createddate, timezone),
          updateddate: formatQueueTime(item.updateddate, timezone)
        };
      }));
      setPostCommands(commands);
      setPostCommandsPage(1);
    } catch (error) {
      setPostCommands([]);
      setPostCommandsError(error.message || 'Unable to load queued commands.');
    } finally {
      setPostCommandsLoading(false);
    }
  };

  const openPostCommands = () => {
    setPostCommandOpen(true);
    setPostCommandView('list');
    fetchPostCommands();
  };

  const editPostCommand = item => {
  setEditingCommandId(item.id);

  setPostCommandForm({
    // command: String(item.command || ''),
    operationDate: moment(item.operationdate).format('YYYY-MM-DDTHH:mm'),
    command: item.command === '95' ? '94' : String(item.command || ''),
    // graceTime: item.gracetime ? item.gracetime.slice(0, 5) : '',
    graceTime: item.gracetime || '',
    status: item.status || 'Active'
  });

  setPostCommandView('edit');
};
const deletePostCommand = async id => {
  if (!window.confirm('Are you sure you want to delete this command?')) {
    return;
  }

  try {
    const response = await fetch(
      `https://alcobrakeadmin.com/v3/app/queuecommands/deletequecommand/${id}`
    );

    if (!response.ok) {
      throw new Error(`Delete failed (${response.status})`);
    }

    fetchPostCommands();
    showAlert('Deleted', 'Command deleted successfully.');
  } catch (error) {
    showAlert(
      'Unable to delete',
      error.message || 'Please try again.',
      false
    );
  }
};
  const submitPostCommand = async (event) => {
    event.preventDefault();
    const { command, operationDate, graceTime, status } = postCommandForm;
    if (!command || !operationDate) return;

    const tagState = String(client?.tagstate || client?.state || client?.rstate || '').toUpperCase();
    const baseConfig = tagState === 'WI' ? '60,50,20' : tagState === 'TX' ? '30,30,25' : '60,30,25';
    const version = Number(firmwareVersion);
    const configResponse = version >= 0.65
      ? `${baseConfig},35,800,30,20,0`
      : version > 0.48 ? `${baseConfig},35,800` : baseConfig;
    const now = moment.utc().format('YYYY-MM-DD HH:mm:ss');

      // Get device timezone
  const timezone = await getQueueTimezone(client.cbid);

  // Convert device local time to UTC
  const operationdate = moment
    .tz(operationDate, 'YYYY-MM-DDTHH:mm', timezone)
    .utc()
    .format('YYYY-MM-DD HH:mm:ss');
    const payload = {
      deviceid: client.cbid,
      clientid: Number(client.id),
      command,
      resp: command === '94' ? configResponse : '',
      // gracetime: graceTime ? `${graceTime}:00` : '00:00:00',
      gracetime: graceTime || '00:00:00',
      refby: 'AlcobrakeCA',
      status,
      operationdate,
      // operationdate: moment(operationDate).utc().format('YYYY-MM-DD HH:mm:ss'),
      createddate: now,
      updateddate: now
    };

    setPostCommandSaving(true);
    try {
    const url = editingCommandId
  ? `https://alcobrakeadmin.com/v3/app/queuecommands/editqueue/${editingCommandId}`
  : 'https://alcobrakeadmin.com/v3/app/queuecommands';

const post = async body => {
  const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error(`Request failed (${response.status})`);
      };
      await post(payload);
      if (command === '94') {
        await post({ ...payload, command: '95', operationdate: moment(payload.operationdate).add(20, 'seconds').format('YYYY-MM-DD HH:mm:ss') });
      }
      setPostCommandSaving(false);
      setPostCommandOpen(false);
      setPostCommandView('list');
      setPostCommandForm({ command: '', operationDate: '', graceTime: '', status: 'Active' });
      setEditingCommandId(null);
      fetchPostCommands();
      showAlert('Command scheduled', 'The command was added to the device queue.');
    } catch (error) {
      setPostCommandSaving(false);
      showAlert('Unable to schedule command', error.message || 'Please try again.', false);
    }
  };
  
  const confirmCommand = async (commandName, commandFn, ...args) => {
    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You want to perform ${commandName} on ${client?.name || 'this'} vehicle`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    });
    
    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await commandFn(...args);
        if (response?.success) {
          showAlert('Success', response.message, true);
        } else {
          showAlert('Device offline', response?.message || `${commandName} failed`, false);
        }
      } catch (error) {
        showAlert('Device offline', error.message || `${commandName} failed`, false);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleResetViolation = () => confirmCommand('RESET VIOLATION', deviceCommands.resetViolation, client, user, oledDevices);
  const handleTakeTest = () => confirmCommand('TAKE TEST', deviceCommands.takeTest, client, user, oledDevices);
  const handleLockDevice = () => confirmCommand('LOCK DEVICE', deviceCommands.lockDevice, client, user, oledDevices);
 const handleGpsChange = async () => {
  const result = await Swal.fire({
    title: 'GPS Interval Configure',

    html: `
      <label
        for="gps-config"
        style="display:block;text-align:left;margin:0 2em"
      >
        GPS Interval (sec)
      </label>

      <div style="position:relative;margin:8px 2em 0;">
        <input
          id="gps-config"
          type="number"
          min="1"
          max="60"
          placeholder="Enter GPS Interval"
          autocomplete="off"
          style="
            width:100%;
            box-sizing:border-box;
            height:42px;
            padding:0 12px;
            border:1px solid #d9d9d9;
            border-radius:4px;
            font-size:16px;
            outline:none;
          "
        >
      </div>
    `,

    showCancelButton: true,
    confirmButtonText: 'Configure',
    cancelButtonText: 'Cancel',
    focusConfirm: false,

    didOpen: () => {
      const popup = Swal.getPopup();
      const gpsInput = popup.querySelector('#gps-config');

      gpsInput.focus();
    },

    preConfirm: () => {
      const popup = Swal.getPopup();

      const gpsInterval = popup
        .querySelector('#gps-config')
        .value
        .trim();

      if (!gpsInterval) {
        Swal.showValidationMessage(
          'Please enter GPS Interval'
        );
        return false;
      }

      if (
        Number(gpsInterval) < 1 ||
        Number(gpsInterval) > 60
      ) {
        Swal.showValidationMessage(
          'GPS Interval must be between 1 and 60 seconds.'
        );
        return false;
      }

      return {
        gpsInterval,
      };
    }
  });

  // User cancelled
  if (!result.isConfirmed) {
    return;
  }

  const { gpsInterval } = result.value;

  setLoading(true);

  try {
    const response = await deviceCommands.gpsChange(
      client,
      user,
      oledDevices,
      gpsInterval
    );

    if (response?.success) {
      showAlert(
        'Success',
        response.message ||
          'GPS Interval configuration command sent successfully',
        true
      );
    } else {
      showAlert(
        'Device offline',
        response?.message ||
          'GPS Interval configuration failed',
        false
      );
    }
  } catch (error) {
    console.error(
      'GPS Interval configuration error:',
      error
    );

    showAlert(
      'Device offline',
      error?.message ||
        'GPS Interval configuration failed',
      false
    );
  } finally {
    setLoading(false);
  }
};
 const handleWifiChange = async () => {
  const result = await Swal.fire({
    title: 'Wi-Fi Configure',
    html: `
      <label
        for="wifi-config-ssid"
        style="display:block;text-align:left;margin:0 2em"
      >
        Wi-Fi SSID
      </label>

     <div style="position:relative;margin:8px 2em 0;">
  <input
    id="wifi-config-ssid"
    placeholder="Enter Wi-Fi SSID"
    autocomplete="off"
    style="
      width:100%;
      box-sizing:border-box;
      height:42px;
      padding:0 12px;
      border:1px solid #d9d9d9;
      border-radius:4px;
      font-size:16px;
      outline:none;
    "
  >
</div>

      <label
        for="wifi-config-password"
        style="display:block;text-align:left;margin:16px 2em 0"
      >
        Wi-Fi Password
      </label>

      <div
        style="
          position:relative;
          margin:8px 2em 0;
        "
      >
        <input
          id="wifi-config-password"
          type="password"
          placeholder="Enter Wi-Fi password"
          autocomplete="new-password"
          style="
            width:100%;
            box-sizing:border-box;
            height:42px;
            padding:0 45px 0 12px;
            border:1px solid #d9d9d9;
            border-radius:4px;
            font-size:16px;
            outline:none;
          "
        >

        <button
          type="button"
          id="wifi-password-toggle"
          title="Show password"
          style="
            position:absolute;
            right:5px;
            top:50%;
            transform:translateY(-50%);
            border:none;
            background:transparent;
            cursor:pointer;
            font-size:20px;
            padding:8px;
          "
        >
          👁️
        </button>
      </div>
    `,

    showCancelButton: true,
    confirmButtonText: 'Configure',
    cancelButtonText: 'Cancel',
    focusConfirm: false,

    didOpen: () => {
      const popup = Swal.getPopup();

      const ssidInput = popup.querySelector('#wifi-config-ssid');
      const passwordInput = popup.querySelector('#wifi-config-password');
      const toggleButton = popup.querySelector('#wifi-password-toggle');

      ssidInput.focus();

      toggleButton.addEventListener('click', () => {
        if (passwordInput.type === 'password') {
          passwordInput.type = 'text';
          toggleButton.innerHTML = '🙈';
          toggleButton.title = 'Hide password';
        } else {
          passwordInput.type = 'password';
          toggleButton.innerHTML = '👁️';
          toggleButton.title = 'Show password';
        }
      });
    },

    preConfirm: () => {
      const popup = Swal.getPopup();

      const ssid = popup
        .querySelector('#wifi-config-ssid')
        .value
        .trim();

      const password = popup
        .querySelector('#wifi-config-password')
        .value;

      if (!ssid || !password) {
        Swal.showValidationMessage(
          'Please enter Wi-Fi SSID and password.'
        );
        return false;
      }
      if(ssid.length < 8){
         Swal.showValidationMessage(
          'Wi-Fi SSID must be at least 8 characters.'
        );
        return false;
      }

       if(password.length < 8){
         Swal.showValidationMessage(
          'Wi-Fi password must be at least 8 characters.'
        );
        return false;
      }

      if (ssid.includes(',') || password.includes(',')) {
        Swal.showValidationMessage(
          'Wi-Fi SSID and password cannot contain commas.'
        );
        return false;
      }

      // Only return entered values.
      // Do NOT call API inside this popup.
      return {
        ssid,
        password
      };
    }
  });

  // User cancelled
  if (!result.isConfirmed) {
    return;
  }

  // At this point Wi-Fi Configure popup is CLOSED.
  const { ssid, password } = result.value;

  setLoading(true);

  try {
    const response = await deviceCommands.wifiChange(
      client,
      user,
      oledDevices,
      ssid,
      password
    );

    if (response?.success) {
      showAlert(
        'Success',
        response.message || 'Wi-Fi configuration command sent successfully',
        true
      );
    } else {
      showAlert(
        'Device offline',
        response?.message || 'Wi-Fi configuration failed',
        false
      );
    }
  } catch (error) {
    console.error('Wi-Fi configuration error:', error);

    showAlert(
      'Device offline',
      error?.message || 'Wi-Fi configuration failed',
      false
    );
  } finally {
    setLoading(false);
  }
};
const handleAucConfigure = async () => {
  const result = await Swal.fire({
    title: 'AUC Configure',
    html: `
      <label
        for="auc-config-auc"
        style="display:block;text-align:left;margin:0 2em"
      >
        AUC
      </label>

      <div style="margin:8px 2em 0;">
        <input
          id="auc-config-auc"
          type="number"
          min="5"
          max="250"
          placeholder="Enter AUC (5-250)"
          style="
            width:100%;
            box-sizing:border-box;
            height:42px;
            padding:0 12px;
            border:1px solid #d9d9d9;
            border-radius:4px;
            font-size:16px;
            outline:none;
          "
        >
      </div>

      <label
        for="auc-config-adc"
        style="display:block;text-align:left;margin:16px 2em 0"
      >
        ADC
      </label>

      <div style="margin:8px 2em 0;">
        <input
          id="auc-config-adc"
          type="number"
          min="0"
          max="127"
          placeholder="Enter ADC (0-127)"
          style="
            width:100%;
            box-sizing:border-box;
            height:42px;
            padding:0 12px;
            border:1px solid #d9d9d9;
            border-radius:4px;
            font-size:16px;
            outline:none;
          "
        >
      </div>
    `,

    showCancelButton: true,
    confirmButtonText: 'Configure',
    cancelButtonText: 'Cancel',
    focusConfirm: false,

    didOpen: () => {
      const popup = Swal.getPopup();

      const aucInput = popup.querySelector('#auc-config-auc');

      aucInput.focus();
    },

    preConfirm: () => {
      const popup = Swal.getPopup();

      const auc = popup
        .querySelector('#auc-config-auc')
        .value
        .trim();

      const adc = popup
        .querySelector('#auc-config-adc')
        .value
        .trim();

      if (auc === '' || adc === '') {
        Swal.showValidationMessage(
          'Please enter AUC and ADC.'
        );
        return false;
      }

      const aucValue = Number(auc);
      const adcValue = Number(adc);

      if (!Number.isInteger(aucValue) || aucValue < 5 || aucValue > 250) {
        Swal.showValidationMessage(
          'AUC must be between 5 and 250.'
        );
        return false;
      }

      if (!Number.isInteger(adcValue) || adcValue < 0 || adcValue > 127) {
        Swal.showValidationMessage(
          'ADC must be between 0 and 127.'
        );
        return false;
      }

      return {
        auc: aucValue,
        adc: adcValue
      };
    }
  });

  // User cancelled
  if (!result.isConfirmed) {
    return;
  }

  const { auc, adc } = result.value;

  setLoading(true);

  try {
    const response = await deviceCommands.aucConfigure(
      client,
      user,
      oledDevices,
      auc,
      adc
    );

    if (response?.success) {
      showAlert(
        'Success',
        response.message || 'AUC configuration command sent successfully',
        true
      );
    } else {
      showAlert(
        'Device offline',
        response?.message || 'AUC configuration failed',
        false
      );
    }
  } catch (error) {
    console.error('AUC configuration error:', error);

    showAlert(
      'Device offline',
      error?.message || 'AUC configuration failed',
      false
    );
  } finally {
    setLoading(false);
  }
};
  const handleInternalInfo = () => confirmCommand('INTERNAL INFO', deviceCommands.internalInfo, client, user, oledDevices);
  const handleUnlockDevice = () => confirmCommand('UNLOCK DEVICE', deviceCommands.unlockDevice, client, user, oledDevices);
  const handleRestartDevice = () => confirmCommand('RESTART DEVICE', deviceCommands.restartDevice, client, user, oledDevices);
  const handleSetOTP = () => confirmCommand('SET OTP', async (...args) => {
    const response = await deviceCommands.setOTP(...args);
    if (!response?.success) return response;

    const restart = await deviceCommands.restartDevice(...args);
    return {
      success: restart.success,
      message: restart.success
        ? 'SET OTP and restart commands sent successfully'
        : `SET OTP succeeded, but restart failed: ${restart.message}`
    };
  }, client, user, oledDevices);
  const handleTakePicture = () => confirmCommand('TAKE PICTURE', deviceCommands.takePicture, client, user, oledDevices);
  const handleBypass = () => confirmCommand('BYPASS', deviceCommands.bypass, client, user, oledDevices);
  const handleUnbypass = () => confirmCommand('UNBYPASS', deviceCommands.unbypass, client, user, oledDevices);
  const handleOneDayPassAction = () => confirmCommand('ONE DAY PASS', deviceCommands.oneDayPass, client, user, oledDevices, 1);
  const deviceMode = String(client?.devicemode || client?.deviceMode || '').trim().toUpperCase();
  const isA300U = ['A300U', 'A200I', 'A200U'].includes(deviceMode);
  const isA300I = deviceMode === 'A300I';
  const commandDeviceId = client ? getTargetDeviceId(client, oledDevices) : undefined;
  const commandRefBy = user?.role === 'office' ? user?.officeId : user?.dealerId;

  const sendExtraCommand = async (label, command, resp) => {
    if (!commandDeviceId) {
      showAlert('Error', 'Device id not found', false);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('https://alcobrakeadmin.com/v3/app/commands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          devId: commandDeviceId,
          command,
          resp,
          refby: commandRefBy,
          role: user?.role
        })
      });

      const text = (await response.text()).trim();
      const ok = text.toLowerCase().includes('operation done');
      showAlert(ok ? 'Success' : 'Device offline', ok ? `${label} command sent successfully` : 'OPERATION NOT DONE', ok);
    } catch (error) {
      showAlert('Device offline', error.message || `${label} failed`, false);
    } finally {
      setLoading(false);
    }
  };

  const promptNumberCommand = async ({ title, label, command, min = 1, max, note }) => {
    const { value } = await Swal.fire({
      title,
      html: `
        ${note ? `<div class="command-note">${note}</div>` : ''}
        <div class="command-field">
          <label>${label}</label>
          <input id="command-value" class="swal2-input" type="number" />
        </div>
      `,
      customClass: { popup: 'device-config-modal command-config-modal' },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const inputValue = Number(document.getElementById('command-value').value);

        if (Number.isNaN(inputValue) || inputValue < min || (max !== undefined && inputValue > max)) {
          Swal.showValidationMessage(`Value must be ${min}${max !== undefined ? ` - ${max}` : ' or greater'}`);
          return false;
        }

        return inputValue;
      }
    });

    if (value !== undefined) {
      await sendExtraCommand(title, command, String(value));
    }
  };

  const promptEnableValueCommand = async ({ title, valueLabel, command, note }) => {
    const { value } = await Swal.fire({
      title,
      html: `
        ${note ? `<div class="command-note">${note}</div>` : ''}
        <div class="command-field">
          <label>Enable / Disable</label>
          <select id="command-enable" class="swal2-input">
            <option value="">Select Option</option>
            <option value="1">Enable (1)</option>
            <option value="0">Disable (0)</option>
          </select>
        </div>
        <div class="command-field">
          <label>${valueLabel}</label>
          <input id="command-value" class="swal2-input" type="number" />
        </div>
      `,
      customClass: { popup: 'device-config-modal command-config-modal' },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const enable = document.getElementById('command-enable').value;
        const inputValue = Number(document.getElementById('command-value').value);

        if (enable === '') {
          Swal.showValidationMessage('Please select Enable or Disable');
          return false;
        }

        if (Number.isNaN(inputValue)) {
          Swal.showValidationMessage('Value must be a number');
          return false;
        }

        return `${enable},${inputValue}`;
      }
    });

    if (value) {
      await sendExtraCommand(title, command, value);
    }
  };

  const promptEnableOnlyCommand = async ({ title, command, note }) => {
    const { value } = await Swal.fire({
      title,
      html: `
        ${note ? `<div class="command-note">${note}</div>` : ''}
        <div class="command-field">
          <label>Select Option</label>
          <select id="command-enable" class="swal2-input">
            <option value="">Select</option>
            <option value="1">Enable (1)</option>
            <option value="0">Disable (0)</option>
          </select>
        </div>
      `,
      customClass: { popup: 'device-config-modal command-config-modal' },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const enable = document.getElementById('command-enable').value;

        if (enable === '') {
          Swal.showValidationMessage('Please select an option');
          return false;
        }

        return enable;
      }
    });

    if (value) {
      await sendExtraCommand(title, command, value);
    }
  };

  const handleAdminSettings = async () => {
    const { value } = await Swal.fire({
      title: 'Admin Settings',
      html: `
        <div class="command-field">
          <label>Wiper Value (0-127)</label>
          <input id="wiper" class="swal2-input" type="number" />
        </div>
        <div class="command-field">
          <label>Alco ADC Cutoff (0-4095)</label>
          <input id="adc" class="swal2-input" type="number" />
        </div>
      `,
      customClass: { popup: 'device-config-modal command-config-modal' },
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const wiper = Number(document.getElementById('wiper').value);
        const adc = Number(document.getElementById('adc').value);

        if (Number.isNaN(wiper) || wiper < 0 || wiper > 127) {
          Swal.showValidationMessage('Wiper must be 0 - 127');
          return false;
        }

        if (Number.isNaN(adc) || adc < 0 || adc > 4095) {
          Swal.showValidationMessage('ADC must be 0 - 4095');
          return false;
        }

        return `${wiper},${adc}`;
      }
    });

    if (value) {
      await sendExtraCommand('Admin Settings', '60', value);
    }
  };

const handleStateRequirements = async () => {
  const serialNumber = String(client?.sno || '').trim().toUpperCase();
  const allowedLockoutProfiles = serialNumber.includes('INDIA')
    ? ['india', 'freshbus']
    : serialNumber.includes('CA')
      ? ['usa']
      : ['india', 'freshbus', 'usa'];
  const updateLockoutButtonState = button => {
    button.disabled = !allowedLockoutProfiles.includes(button.dataset.lockout);
    button.style.opacity = button.disabled ? '0.55' : '1';
    button.style.cursor = button.disabled ? 'not-allowed' : 'pointer';
  };

  const labels = [
    'Proxy Enable(CO₂)',
    'Barometric Enable(Altitude)',
    'Temperature Enable',
    'Fail Count',
    'Refused Count',
    'Engine Off Timer(mins)',
    'Car_Battery_Tampering(mins)',
    'Insufficient Count(mins)',
    'First RR Test(mins)',
    'Lockout Days',
    'Calibration Grace',
    'Second RR Test(mins)',
    'Initial Lockout',
    'First Initial Fail Lockout Timer(mins)',
    'Second Initial Fail Lockout Timer(mins)',
    'Display Results',
    'Random Test Fail/Refuse Timer(mins)',
    'Calibration Days',
    'Calibrated Value',
    'Alcohol Cutoff',
    'Blow Volume',
    'Blow Time',
    'Breathe Min',
    'Breathe Max',
    'Random Test',
    'Lockout Disable/Enable',
    'Lockout Enable Value'
  ];

  const stateCategories = {
  'Device Settings': [
    12, // Initial Lockout
    13, // First Initial Fail Lockout Timer
    14, // Second Initial Fail Lockout Timer
    24, // Random Test
    8,  // First RR Test
    11, // Second RR Test
    16, // Random Test Fail/Refuse Timer
    18, // Calibrated Value
    19, // Alcohol Cutoff
    20, // Blow Volume
    21, // Blow Time
    15, // Display Results
    22, // Breathe Min
    23, // Breathe Max
    3,  // Fail Count
    4,  // Refused Count
    7,  // Insufficient Count
    5   // Engine Off Timer
  ],

  'Sensor Settings': [
    0, // Proxy Enable
    1, // Barometric Enable
    2  // Temperature Enable
  ],

  'Lockout Settings': [
    9,  // Lockout Days
    10, // Calibration Grace
    17, // Calibration Days
    6   // Battery Tamp
  ]
};
  

  // Fields which should be dropdowns
  const stateSelectIndexes = new Set([
    0,
    1,
    2,
    12,
    15,
    17, // Calibration Days -> 30 / 60
    24  // Random Test
  ]);

  // Min / Max validation for text fields
  const stateValidationRanges = {
     3: {
    min: 0,
    max: 5,
    label: 'Fail Count'
  },

  4: {
    min: 0,
    max: 5,
    label: 'Refused Count'
  },

  5: {
    min: 1,
    max: 45,
    label: 'Engine Off Timer(mins)'
  },

  6: {
    min: 1,
    max: 30,
    label: 'Battery Tamp(mins)'
  },

  7: {
    min: 0,
    max: 10,
    label: 'Insufficient Count(mins)'
  },
    8: {
      min: 1,
      max: 4320,
      label: 'First RR Test(mins)'
    },

    9: {
      min: 1,
      max: 7,
      label: 'LC Days'
    },

    10: {
      min: 1,
      max: 10,
      label: 'Calibration Grace'
    },

    11: {
      min: 45,
      max: 4320,
      label: 'Second RR Test(mins)'
    },

    13: {
      min: 1,
      max: 60,
      label: 'First Initial Fail Lockout Timer(mins)'
    },

    14: {
      min: 1,
      max: 60,
      label: 'Second Initial Fail Lockout Timer(mins)'
    },

    16: {
      min: 0,
      max: 45,
      label: 'Random Test Fail/Refuse Timer(mins)'
    },

    18: {
      min: 10,
      max: 50,
      label: 'Alcohol Sol'
    },

    19: {
      min: 10,
      max: 40,
      label: 'Alcohol Cutoff'
    },

    20: {
      min: 20,
      max: 80,
      label: 'Blow Volume'
    },

    21: {
      min: 800,
      max: 1400,
      label: 'Blow Time'
    },

    22: {
      min: 800,
      max: 1700,
      label: 'Breathe Min'
    },

    23: {
      min: 2000,
      max: 5000,
      label: 'Breathe Max'
    }
  };

  const defaultStateValues = [
    0,
    0,
    0,
    1,
    1,
    5,
    20,
    7,
    7,
    5,
    7,
    45,
    0,
    6,
    1,
    1,
    30,
    25,
    40,
    400,
    1700,
    800,
    0,
    2600,
    0,
    2,
    0
  ];

  //   const configuredDefaultStateValues = {
//     india: [
//       0,    // Proxy Enable
//       0,    // Barometric Enable
//       0,    // Temperature Enable
//       3,    // Fail Count
//       3,    // Refused Count
//       30,   // Engine Off Timer
//       20,   // Battery Tamp
//       5,    // Insufficient Count
//       180,  // First RR Test
//       5,    // LC Days
//       7,    // Calibration Grace
//       180,  // Second RR Test
//       0,    // Initial Lockout
//       30,   // First Initial Fail Lockout
//       0,    // Second Initial Fail Lockout
//       1,    // Display Results
//       3,    // Random Test Fail/Refuse Timer
//       60,   // Calibration Days
//       30,   // Alcohol Sol
//       25,   // Alcohol Cutoff
//       40, // Blow Volume
//       800,   // Blow Time
//       80,    // Breathe Min
//       2600,  // Breathe Max
//       2,     // Lockout Disable/Enable
//       0      // Lockout Enable Value
//     ],

//     freshbus: [
//       0,    // Proxy Enable
//       0,    // Barometric Enable
//       0,    // Temperature Enable
//       3,    // Fail Count
//       3,    // Refused Count
//       30,   // Engine Off Timer
//       20,   // Battery Tamp
//       5,    // Insufficient Count
//       180,  // First RR Test
//       5,    // LC Days
//       7,    // Calibration Grace
//       180,  // Second RR Test
//       0,    // Initial Lockout
//       30,   // First Initial Fail Lockout
//       0,    // Second Initial Fail Lockout
//       1,    // Display Results
//       3,    // Random Test Fail/Refuse Timer
//       60,   // Calibration Days
//       30,   // Alcohol Sol
//       25,   // Alcohol Cutoff
//       40, // Blow Volume
//       800,   // Blow Time
//       80,    // Breathe Min
//       2600,  // Breathe Max
//       2,     // Lockout Disable/Enable
//       1      // Lockout Enable Value
//     ],

//     usa: [
//       0,    // Proxy Enable
//       0,    // Barometric Enable
//       0,    // Temperature Enable
//       3,    // Fail Count
//       3,    // Refused Count
//       3,    // Engine Off Timer
//       20,   // Battery Tamp
//       5,    // Insufficient Count
//       5,    // First RR Test
//       5,    // LC Days
//       7,    // Calibration Grace
//       45,   // Second RR Test
//       0,    // Initial Lockout
//       15,   // First Initial Fail Lockout
//       30,   // Second Initial Fail Lockout
//       1,    // Display Results
//       3,    // Random Test Fail/Refuse Timer
//       60,   // Calibration Days
//       30,   // Alcohol Sol
//       25,   // Alcohol Cutoff
//       40, // Blow Volume
//       800,   // Blow Time
//       80,    // Breathe Min
//       2600,  // Breathe Max
//       1,     // Lockout Disable/Enable
//       1      // Lockout Enable Value
//     ]
//   };

  const configuredDefaultStateValues = {
    india: [
      0, 0, 0, 3, 3, 30, 20, 5, 180, 5, 7, 180,
      0, 15, 30, 1, 3, 60, 30, 25, 40, 800, 1700, 2600,
      0, 2, 0
    ],

    freshbus: [
      0, 0, 0, 3, 3, 30, 20, 5, 180, 5, 7, 180,
      0, 15, 30, 1, 3, 60, 30, 25, 40, 800, 1700, 2600,
      0, 2, 1
    ],

    usa: [
      0, 0, 0, 3, 3, 3, 20, 5, 7, 5, 7, 45,
      0, 15, 30, 1, 3, 60, 30, 25, 40, 800, 1700, 2600,
      0, 1, 1
    ]
  };

  const getStateValue = (arr, index, fallback) => {
    const value = Number(arr?.[index]);

    return Number.isFinite(value)
      ? value
      : fallback;
  };

  const loadStateRequirementValues = async () => {
    if (!commandDeviceId) {
      return {
        values: defaultStateValues,
        profile: 'india'
      };
    }

    try {
      const response = await fetch(
        `https://alcobrakeadmin.com/v3/v2/caltrack/getlatestcbidcaltrack?cbid=${client?.cbid || client?.devId}`
      );

      const data = await response.json();

      const fullresp =
        data?.[0]?.fullresp?.String ||
        data?.[0]?.fullresp?.string ||
        data?.[0]?.fullresp;

      if (!fullresp || typeof fullresp !== 'string') {
        return {
          values: defaultStateValues,
          profile: 'india'
        };
      }

      const arr = fullresp.split(',');

      const deviceType =
        String(arr[arr.length - 1] || '').trim();

      let profile = 'india';

      if (deviceType === 'A200I') {
        profile = 'india';
      } else if (deviceType === 'A200U') {
        profile = 'usa';
      } else if (deviceType === 'A200FB') {
        profile = 'freshbus';
      }

      const values = [
        // 0 - Proxy Enable
        getStateValue(arr, 15, defaultStateValues[0]),

        // 1 - Barometric Enable
        getStateValue(arr, 16, defaultStateValues[1]),

        // 2 - Temperature Enable
        getStateValue(arr, 17, defaultStateValues[2]),

        // 3 - Fail Count
        getStateValue(arr, 18, defaultStateValues[3]),

        // 4 - Refused Count
        getStateValue(arr, 19, defaultStateValues[4]),

        // 5 - Engine Off Timer
        getStateValue(arr, 20, defaultStateValues[5]),

        // 6 - Battery Tamp
        getStateValue(arr, 21, defaultStateValues[6]),

        // 7 - Insufficient Count
        getStateValue(arr, 22, defaultStateValues[7]),

        // 8 - First RR Test
        getStateValue(arr, 23, defaultStateValues[8]),

        // 9 - LC Days
        getStateValue(arr, 24, defaultStateValues[9]),

        // 10 - Calibration Grace
        getStateValue(arr, 25, defaultStateValues[10]),

        // 11 - Second RR Test
        getStateValue(arr, 26, defaultStateValues[11]),

        // 12 - Initial Lockout
        getStateValue(arr, 27, defaultStateValues[12]),

        // 13 - First Initial Fail Lockout Timer
        getStateValue(arr, 28, defaultStateValues[13]),

        // 14 - Second Initial Fail Lockout Timer
        getStateValue(arr, 29, defaultStateValues[14]),

        // 15 - Display Results
        getStateValue(arr, 30, defaultStateValues[15]),

        // 16 - Random Test Fail/Refuse Timer
        getStateValue(arr, 31, defaultStateValues[16]),

        // 17 - Calibration Days
        getStateValue(arr, 32, defaultStateValues[17]),

        // 18 - Alcohol Sol
        getStateValue(arr, 33, defaultStateValues[18]),

        // 19 - Alcohol Cutoff
        getStateValue(arr, 34, defaultStateValues[19]),

        // 20 - Blow Volume
        getStateValue(arr, 35, defaultStateValues[20]),

        // 21 - Blow Time
        getStateValue(arr, 36, defaultStateValues[21]),

        // 22 - Breathe Min
        getStateValue(arr, 37, defaultStateValues[22]),

        // 23 - Breathe Max
        getStateValue(arr, 38, defaultStateValues[23]),

        // 24 - Random Test
        getStateValue(arr, 39, defaultStateValues[24]),

        // 25 - Lockout Disable/Enable
        getStateValue(arr, 40, defaultStateValues[25]),

        // 26 - Lockout Enable Value
        getStateValue(arr, 41, defaultStateValues[26])
      ];

      return {
        values,
        profile
      };

    } catch (error) {
      console.log('State Requirements load failed');

      return {
        values: defaultStateValues,
        profile: 'india'
      };
    }
  };

  // -------------------------------------------------------------
  // Loading popup
  // -------------------------------------------------------------

  Swal.fire({
    title: 'State Requirements',
    text: 'Please wait...',
    allowOutsideClick: false,
    showConfirmButton: false,

    customClass: {
      popup:
        'device-config-modal command-config-modal'
    },

    didOpen: () => Swal.showLoading()
  });

  const stateResult =
    await loadStateRequirementValues();

  const stateValues =
    stateResult.values;

  const detectedProfile = allowedLockoutProfiles.includes(stateResult.profile)
    ? stateResult.profile
    : allowedLockoutProfiles[0];

  Swal.close();

  // -------------------------------------------------------------
  // State Requirements dialog
  // -------------------------------------------------------------

  const { value } = await Swal.fire({
  title: 'State Requirements',

  width: '850px',

  html: `

    <!-- =========================================
         DEFAULT CONFIG
         ========================================= -->

    <div class="state-default-toggle-wrapper">

      <span>Default Configs</span>

      <label class="state-toggle">

        <input
          type="checkbox"
          id="state-default-config-toggle"
        />

        <span class="state-toggle-slider"></span>

      </label>

    </div>


    <!-- =========================================
         LOCKOUT PROFILE
         ========================================= -->

    <div class="lockout-profile-box">

      <div class="lockout-profile-title">

        <span>Lockout Profile</span>

        <small>
          Select configuration
        </small>

      </div>


      <div class="lockout-profile-options">

        <button
          type="button"
          class="lockout-profile-btn"
          data-lockout="india"
        >
          <span>🇮🇳</span>
          <strong>India</strong>
          <small>2, 0</small>
        </button>


        <button
          type="button"
          class="lockout-profile-btn"
          data-lockout="freshbus"
        >
          <span>🇮🇳</span>
          <strong>FreshBus</strong>
          <small>2, 1</small>
        </button>


        <button
          type="button"
          class="lockout-profile-btn"
          data-lockout="usa"
        >
          <span>🇺🇸</span>
          <strong>USA</strong>
          <small>1, 1</small>
        </button>

      </div>


      <!-- Hidden values -->

      <input
        type="hidden"
        id="state-25"
        value="2"
      />

      <input
        type="hidden"
        id="state-26"
        value="0"
      />

    </div>


    <!-- =========================================
         SETTINGS SCROLL AREA
         ========================================= -->

    <div class="state-settings-container">


      <!-- =======================================
           DEVICE SETTINGS
           ======================================= -->

      <div class="state-category-card">

        <div class="state-category-header">

          <div class="state-category-icon">
            ⚙
          </div>

          <div class="state-category-heading">

            <div class="state-category-title">


              Device Settings

            </div>

            <div class="state-category-subtitle">
              Device operation and test parameters
            </div>

          </div>

        </div>


        <div class="state-category-body">

          ${stateCategories['Device Settings'].map(index => {

            const label = labels[index];

            const range =
              stateValidationRanges[index];

            return `

              <div
                class="command-field"
                id="state-field-wrapper-${index}"
              >

                <label>

                  ${label}

                  ${
                    range
                      ? `
                        <span class="field-range">
                          Range: ${range.min} - ${range.max}
                        </span>
                      `
                      : ''
                  }

                </label>


                ${
                  stateSelectIndexes.has(index)

                    ? `

                      <select
                        id="state-${index}"
                        class="swal2-input"
                      >

                        ${
                          index === 24

                            ? `
                              <option value="0">
                                Enable
                              </option>

                              <option value="1">
                                Disable
                              </option>
                            `

                            : index === 17

                              ? `
                                <option value="30">
                                  30 Days
                                </option>

                                <option value="60">
                                  60 Days
                                </option>

                                <option value="90">
                                  90 Days
                                </option>
                              `

                              : `
                                <option value="0">
                                  Disable
                                </option>

                                <option value="1">
                                  Enable 
                                </option>
                              `
                        }

                      </select>

                    `

                    : `

                      <input
                        id="state-${index}"
                        class="swal2-input"
                        type="number"

                        min="${
                          range
                            ? range.min
                            : ''
                        }"

                        max="${
                          range
                            ? range.max
                            : ''
                        }"

                        value="${
                          stateValues[index] ??
                          defaultStateValues[index]
                        }"
                          oninput="if (this.value > ${range ? range.max : 'Infinity'}) this.value = ${range ? range.max : ''}"
                      />

                    `
                }

              </div>

            `;

          }).join('')}

        </div>

      </div>


      <!-- =======================================
           SENSOR SETTINGS
           ======================================= -->

      <div class="state-category-card">

        <div class="state-category-header">

          <div class="state-category-icon">
            ◉
          </div>

          <div class="state-category-heading">

            <div class="state-category-title">


              Sensor Settings

            </div>

            <div class="state-category-subtitle">
              Environmental sensor configuration
            </div>

          </div>

        </div>


        <div class="state-category-body">

          ${stateCategories['Sensor Settings'].map(index => {

            const label = labels[index];

            return `

              <div
                class="command-field"
                id="state-field-wrapper-${index}"
              >

                <label>
                  ${label}
                </label>


                <select
                  id="state-${index}"
                  class="swal2-input"
                >

                  <option value="0">
                    Disable
                  </option>

                  <option value="1">
                    Enable
                  </option>

                </select>

              </div>

            `;

          }).join('')}

        </div>

      </div>


      <!-- =======================================
           LOCKOUT SETTINGS
           ======================================= -->

      <div class="state-category-card">

        <div class="state-category-header">

          <div class="state-category-icon">
            🔒
          </div>

          <div class="state-category-heading">

            <div class="state-category-title">


              Lockout Settings

            </div>

            <div class="state-category-subtitle">
              Lockout and calibration configuration
            </div>

          </div>

        </div>


        <div class="state-category-body">

          ${stateCategories['Lockout Settings'].map(index => {

            const label = labels[index];

            const range =
              stateValidationRanges[index];

            return `

              <div
                class="command-field"
                id="state-field-wrapper-${index}"
              >

                <label>

                  ${label}

                  ${
                    range
                      ? `
                        <span class="field-range">
                          Range: ${range.min} - ${range.max}
                        </span>
                      `
                      : ''
                  }

                </label>


                ${
                  stateSelectIndexes.has(index)

                    ? `

                      <select
                        id="state-${index}"
                        class="swal2-input"
                      >

                        <option value="30">
                          30 Days
                        </option>

                        <option value="60">
                          60 Days
                        </option>

                        <option value="90">
                          90 Days
                        </option>

                      </select>

                    `

                    : `

                      <input
                        id="state-${index}"
                        class="swal2-input"
                        type="number"

                        min="${
                          range
                            ? range.min
                            : ''
                        }"

                        max="${
                          range
                            ? range.max
                            : ''
                        }"

                        value="${
                          stateValues[index] ??
                          defaultStateValues[index]
                        }"
                          oninput="if (this.value > ${range ? range.max : 'Infinity'}) this.value = ${range ? range.max : ''}"
                      />

                    `
                }

              </div>

            `;

          }).join('')}

        </div>

      </div>


    </div>

  `,

  customClass: {
    popup:
      'device-config-modal command-config-modal state-requirements-modal compact-state-modal'
  },

  showCancelButton: true,

  confirmButtonText: 'Submit',

  cancelButtonText: 'Cancel',

    didOpen: () => {

      const randomTestSelect = document.getElementById('state-24');
const firstRRTest = document.getElementById('state-8');
const secondRRTest = document.getElementById('state-11');

function updateRandomTestFields() {
  const enabled = randomTestSelect?.value === '0';

  firstRRTest.disabled = !enabled;
  secondRRTest.disabled = !enabled;
}

randomTestSelect?.addEventListener('change', updateRandomTestFields);
updateRandomTestFields();

      const defaultToggle =
        document.getElementById(
          'state-default-config-toggle'
        );

      const initialLockoutSelect =
        document.getElementById(
          'state-12'
        );

      const firstInitialFailInput =
        document.getElementById(
          'state-13'
        );

      const secondInitialFailInput =
        document.getElementById(
          'state-14'
        );

      const lockoutDisable =
        document.getElementById(
          'state-25'
        );

      const lockoutEnableValue =
        document.getElementById(
          'state-26'
        );

      let selectedLockoutProfile =
        detectedProfile || 'india';

      // ---------------------------------------------------------
      // Update selected lockout profile
      // ---------------------------------------------------------

      const updateSelectedProfileUI =
        (profile) => {
          if (!allowedLockoutProfiles.includes(profile)) return;

          selectedLockoutProfile =
            profile;

          document
            .querySelectorAll(
              '.lockout-profile-btn'
            )
            .forEach(button => {

              button.classList.toggle(
                'active',
                button.dataset.lockout === profile
              );

            });

          switch (profile) {

            case 'india':

              if (lockoutDisable) {
                lockoutDisable.value = '2';
              }

              if (lockoutEnableValue) {
                lockoutEnableValue.value = '0';
              }

              break;

            case 'freshbus':

              if (lockoutDisable) {
                lockoutDisable.value = '2';
              }

              if (lockoutEnableValue) {
                lockoutEnableValue.value = '1';
              }

              break;

            case 'usa':

              if (lockoutDisable) {
                lockoutDisable.value = '1';
              }

              if (lockoutEnableValue) {
                lockoutEnableValue.value = '1';
              }

              break;

            default:

              if (lockoutDisable) {
                lockoutDisable.value = '2';
              }

              if (lockoutEnableValue) {
                lockoutEnableValue.value = '0';
              }

          }
        };

      // ---------------------------------------------------------
      // Initial Lockout enable / disable
      // ---------------------------------------------------------

      const updateInitialLockoutFields =
        () => {

          const enabled =
            initialLockoutSelect?.value === '1';

          if (firstInitialFailInput) {

            firstInitialFailInput.disabled =
              !enabled;

            firstInitialFailInput.style.opacity =
              enabled ? '1' : '0.55';

            firstInitialFailInput.style.cursor =
              enabled
                ? 'text'
                : 'not-allowed';
          }

          if (secondInitialFailInput) {

            secondInitialFailInput.disabled =
              !enabled;

            secondInitialFailInput.style.opacity =
              enabled ? '1' : '0.55';

            secondInitialFailInput.style.cursor =
              enabled
                ? 'text'
                : 'not-allowed';
          }
        };

      // ---------------------------------------------------------
      // Apply profile defaults
      // ---------------------------------------------------------

      const applyProfileDefaults =
        (profile) => {

          const values =
            configuredDefaultStateValues[
              profile
            ];

          if (!values) return;

          values.forEach(
            (value, index) => {

              const element =
                document.getElementById(
                  `state-${index}`
                );

              if (element) {
                element.value =
                  String(value);
              }

            }
          );

          updateSelectedProfileUI(
            profile
          );

          updateInitialLockoutFields();
        };

      // ---------------------------------------------------------
      // Restore Caltrack values
      // ---------------------------------------------------------

      const restoreLoadedValues =
        () => {

          labels.forEach(
            (_, index) => {

              const element =
                document.getElementById(
                  `state-${index}`
                );

              if (!element) return;

              let value =
                stateValues[index];

              if (
                value === undefined ||
                value === null ||
                value === '' ||
                !Number.isFinite(
                  Number(value)
                )
              ) {
                value =
                  defaultStateValues[
                    index
                  ];
              }

              element.value =
                String(value);
            }
          );

          updateSelectedProfileUI(
            selectedLockoutProfile
          );

          updateInitialLockoutFields();
        };

      // ---------------------------------------------------------
      // Disable / enable fields
      // ---------------------------------------------------------

      const updateAllFieldsState =
        (disabled) => {

          document
            .querySelectorAll(
              '.command-field select, .command-field input'
            )
            .forEach(element => {

              element.disabled =
                disabled;

              element.style.opacity =
                disabled
                  ? '0.55'
                  : '1';

              element.style.cursor =
                disabled
                  ? 'not-allowed'
                  : '';

            });

          document
            .querySelectorAll(
              '.lockout-profile-btn'
            )
            .forEach(button => {

              updateLockoutButtonState(button);

            });
        };

      // ---------------------------------------------------------
      // Compare current values with profile defaults
      // ---------------------------------------------------------

      const areValuesSameAsDefaults =
        (profile) => {

          const defaults =
            configuredDefaultStateValues[
              profile
            ];

          if (
            !defaults ||
            !stateValues
          ) {
            return false;
          }

          return defaults.every(
            (defaultValue, index) => {

              const deviceValue =
                Number(
                  stateValues[index]
                );

              const defaultValueNumber =
                Number(
                  defaultValue
                );

              if (
                !Number.isFinite(
                  deviceValue
                ) ||
                !Number.isFinite(
                  defaultValueNumber
                )
              ) {
                return false;
              }

              return (
                deviceValue ===
                defaultValueNumber
              );
            }
          );
        };

      // ---------------------------------------------------------
      // Default toggle state
      // ---------------------------------------------------------

      const updateDefaultToggleState =
        () => {

          if (!defaultToggle) return;

          const isDefault =
            areValuesSameAsDefaults(
              selectedLockoutProfile
            );

          defaultToggle.checked =
            isDefault;

          updateAllFieldsState(
            isDefault
          );

          document
            .querySelectorAll(
              '.lockout-profile-btn'
            )
            .forEach(button => {

              updateLockoutButtonState(button);

            });
        };

      // ---------------------------------------------------------
      // LOCKOUT PROFILE BUTTONS
      // ---------------------------------------------------------

      document
        .querySelectorAll(
          '.lockout-profile-btn'
        )
        .forEach(button => {

          button.addEventListener(
            'click',
            () => {

              const profile =
                button.dataset.lockout;
              if (!allowedLockoutProfiles.includes(profile)) return;

              selectedLockoutProfile =
                profile;

              updateSelectedProfileUI(
                profile
              );

              applyProfileDefaults(
                profile
              );

              if (
                defaultToggle?.checked
              ) {
                updateAllFieldsState(
                  true
                );
              } else {
                updateAllFieldsState(
                  false
                );
              }

              updateInitialLockoutFields();
            }
          );
        });

      // ---------------------------------------------------------
      // INITIAL PROFILE
      // ---------------------------------------------------------

      updateSelectedProfileUI(
        selectedLockoutProfile
      );

      // Restore actual Caltrack values
      restoreLoadedValues();

      // ---------------------------------------------------------
      // DEFAULT CONFIG TOGGLE
      // ---------------------------------------------------------

      if (defaultToggle) {

        updateDefaultToggleState();

        defaultToggle.addEventListener(
          'change',
          () => {

            if (
              defaultToggle.checked
            ) {

              applyProfileDefaults(
                selectedLockoutProfile
              );

              updateAllFieldsState(
                true
              );

            } else {

              restoreLoadedValues();

              updateAllFieldsState(
                false
              );

              updateInitialLockoutFields();
            }

            document
              .querySelectorAll(
                '.lockout-profile-btn'
              )
              .forEach(button => {

                updateLockoutButtonState(button);

              });

          }
        );
      }

      // ---------------------------------------------------------
      // INITIAL LOCKOUT CHANGE
      // ---------------------------------------------------------

      if (initialLockoutSelect) {

        initialLockoutSelect.addEventListener(
          'change',
          updateInitialLockoutFields
        );
      }

      updateInitialLockoutFields();

      if (defaultToggle?.checked) {

        updateAllFieldsState(true);

      } else {

        updateAllFieldsState(false);
      }

      document
        .querySelectorAll(
          '.lockout-profile-btn'
        )
        .forEach(button => {

          updateLockoutButtonState(button);

        });

    },

    // -----------------------------------------------------------
    // PRE CONFIRM
    // -----------------------------------------------------------

    preConfirm: () => {

      const values =
        labels.map(
          (_, index) => {

            const element =
              document.getElementById(
                `state-${index}`
              );

            return element
              ? element.value.trim()
              : '';
          }
        );

      // ---------------------------------------------------------
      // LOCKOUT VALUES
      // ---------------------------------------------------------

      values[25] =
        document
          .getElementById('state-25')
          .value;

      values[26] =
        document
          .getElementById('state-26')
          .value;

      const submittedProfile = values[25] === '1' && values[26] === '1'
        ? 'usa'
        : values[25] === '2' && values[26] === '0'
          ? 'india'
          : values[25] === '2' && values[26] === '1' ? 'freshbus' : '';
      if (!allowedLockoutProfiles.includes(submittedProfile)) {
        Swal.showValidationMessage('Select a Lockout Profile allowed for this device serial number.');
        return false;
      }

      // ---------------------------------------------------------
      // REQUIRED VALIDATION
      // ---------------------------------------------------------

      if (
        values.some(
          item => item === ''
        )
      ) {

        Swal.showValidationMessage(
          'All fields are required'
        );

        return false;
      }

      // ---------------------------------------------------------
      // NUMBER VALIDATION
      // ---------------------------------------------------------

      if (
        values.some(
          item =>
            Number.isNaN(
              Number(item)
            )
        )
      ) {

        Swal.showValidationMessage(
          'All values must be numbers'
        );

        return false;
      }

      // ---------------------------------------------------------
      // MIN / MAX VALIDATION
      // ---------------------------------------------------------

      for (
        let index = 0;
        index < labels.length;
        index++
      ) {

        const range =
          stateValidationRanges[
            index
          ];

        if (!range) {
          continue;
        }

        const value =
          Number(values[index]);

        if (
          value < range.min ||
          value > range.max
        ) {

          Swal.showValidationMessage(
            `${range.label} must be between ${range.min} and ${range.max}`
          );

          return false;
        }
      }

      // ---------------------------------------------------------
      // CALIBRATION DAYS
      // Only 30 or 60
      // ---------------------------------------------------------

      if (
        values[17] !== '30' &&
        values[17] !== '60' && values[17] !== '90'
      ) {

        Swal.showValidationMessage(
          'Calibration Days must be 30 or 60 or 90'
        );

        return false;
      }

      // ---------------------------------------------------------
      // RANDOM TEST
      // 0 = Enable
      // 1 = Disable
      // ---------------------------------------------------------

      if (
        values[24] !== '0' &&
        values[24] !== '1'
      ) {

        Swal.showValidationMessage(
          'Random Test must be Enable or Disable'
        );

        return false;
      }

      // ---------------------------------------------------------
      // FINAL COMMAND
      // ---------------------------------------------------------

      return values.join(',');
    }
  });

  // -------------------------------------------------------------
  // SEND STATE REQUIREMENTS COMMAND
  // -------------------------------------------------------------

  if (value) {

    await sendExtraCommand(
      'State Requirements',
      '50',
      value
    );
  }
};

  const requestStorageClearCredentials = async (title, confirmButtonText) => {
    const { value } = await Swal.fire({
      title,
      html: `
        <div class="clear-storage-dialog">
          <div class="warning-box">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p><strong>Warning:</strong> This will permanently delete all logs and pending commands from the database.</p>
          </div>
          <div class="info-text"><p>This action cannot be undone. All device history will be lost.</p></div>
          <div class="input-group">
            <label for="swal-email">Dealer Email</label>
            <input id="swal-email" class="swal2-input" type="email" value="alcobrakeca@gmail.com" placeholder="Enter dealer email" autocomplete="off">
          </div>
          <div class="input-group">
            <label for="swal-password">Dealer Password</label>
            <div class="password-field-with-toggle">
              <input id="swal-password" class="swal2-input" type="password" value="Alcobrake_2024" placeholder="Enter dealer password">
              <button id="swal-password-toggle" class="password-visibility-toggle" type="button" aria-label="Show password" title="Show password">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>
              </button>
            </div>
          </div>
        </div>
      `,
      width: '450px',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      didOpen: () => {
        const passwordInput = document.getElementById('swal-password');
        const passwordToggle = document.getElementById('swal-password-toggle');
        passwordToggle?.addEventListener('click', () => {
          if (!passwordInput) return;
          const shouldShow = passwordInput?.type === 'password';
          passwordInput.type = shouldShow ? 'text' : 'password';
          passwordToggle.setAttribute('aria-label', shouldShow ? 'Hide password' : 'Show password');
          passwordToggle.setAttribute('title', shouldShow ? 'Hide password' : 'Show password');
        });
      },
      preConfirm: () => {
        const email = document.getElementById('swal-email').value;
        const password = document.getElementById('swal-password').value;
        if (!email || !password) {
          Swal.showValidationMessage('Please enter both email and password');
          return false;
        }
        if (!email.includes('@') || !email.includes('.')) {
          Swal.showValidationMessage('Please enter a valid email address');
          return false;
        }
        return { email, password };
      }
    });
    return value;
  };

  const handleClearStorage = async () => {
    if (user.role !== 'Admin' && user.role !== 'superadmin') {
      Swal.fire({
        title: 'Access Denied',
        text: 'Only administrators can clear device storage',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }
    
    const formValues = await requestStorageClearCredentials(
      'Clear Device Storage',
      'Yes, Clear Storage'
    );
    
    if (formValues) {
      setLoading(true);
      setProgress('Deleting logs from database...');
      
      Swal.fire({
        title: 'Processing...',
        text: 'Please wait while we clear the device storage.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      try {
        const response = await deviceCommands.clearStorage(
          client, 
          user, 
          oledDevices, 
          formValues.email, 
          formValues.password
        );
        
        Swal.close();
        
        if (response?.success) {
          Swal.fire({
            title: 'Success!',
            text: response.message,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#10b981'
          });
          
          setTimeout(() => {
            fetchDeviceInfo();
            fetchConnectionStatus();
            fetchOTPs();
          }, 1500);
          
        } else {
          Swal.fire({
            title: 'Device offline',
            text: response?.message || 'Clear storage failed',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#dc2626'
          });
        }
      } catch (error) {
        Swal.close();
        Swal.fire({
          title: 'Device offline',
          text: error.message || 'Clear storage failed. Please check your credentials.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc2626'
        });
      } finally {
        setLoading(false);
        setProgress('');
      }
    }
  };
  
  const showDeviceInfoResult = (data, mode, source) => {
    const entries = Object.entries(data);
    const blankParameters = entries
      .filter(([, value]) => value === '' || value === null || value === undefined)
      .map(([parameter]) => parameter);
    const rawParameterCount = data.__rawParameterCount || entries.length;

    console.groupCollapsed(`[Device Info] ${source}`);
    console.log('Device mode:', mode || 'Unknown');
    console.log('Parameters displayed:', entries.length);
    if (mode === 'A200U') {
      console.log(
        `A200U response fields: ${rawParameterCount}; UI parameters displayed: ${entries.length}/34; ${blankParameters.length} blank value(s).`
      );
      if (blankParameters.length) console.warn('Blank A200U parameters:', blankParameters);
    }
    console.table(entries.map(([parameter, value]) => ({ parameter, value })));
    console.groupEnd();

    setDeviceInfoCommandMode(mode);
    setDeviceInfoCommandData(data);
    setDeviceInfoModalOpen(true);
  };

  // const handleDeviceInfoPreview = () => {
  //   showDeviceInfoResult(A200U_DEVICE_INFO_PREVIEW, 'A200U', 'Preview sample (no device command sent)');
  // };

  const handleDeviceInfo = async () => {
    const result = await Swal.fire({
      title: 'Get Device Information?',
      text: `Request the latest device information from ${client?.regnum || client?.regNum || client?.sno || 'this device'}?`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Yes, Get Info',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2563eb'
    });
    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const response = await deviceCommands.deviceInfo(client, user, oledDevices);
      if (response?.success && response.data) {
        const mode = String(
          client?.devicemode || client?.deviceMode || client?.devicemodel || ''
        ).trim().toUpperCase();
        showDeviceInfoResult(response.data, mode, 'Command response');
      } else {
        showAlert('Device offline', response?.message || 'Failed to get device info', false);
      }
    } catch (error) {
      showAlert('Device offline', error.message || 'Failed to get device info', false);
    } finally {
      setLoading(false);
    }
  };
  
  const handleMemoryReset = async () => {
    const result = await Swal.fire({
      title: 'Memory Reset',
      text: 'This will upload logs and reset device memory. Continue?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    });
    
    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await deviceCommands.memoryReset(client, user, oledDevices, setProgress);
        if (response?.success) {
          showAlert('Success', response.message, true);
        } else {
          showAlert('Device offline', response?.message || 'Memory reset failed', false);
        }
      } catch (error) {
        showAlert('Device offline', error.message || 'Memory reset failed', false);
      } finally {
        setLoading(false);
        setProgress('');
      }
    }
  };
  
  const handleReadLogs = async () => {
    const result = await Swal.fire({
      title: 'Read Logs',
      text: 'This will upload missed logs from the device. Continue?',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Yes, Read Logs',
      cancelButtonText: 'Cancel'
    });
    
    if (result.isConfirmed) {
      setLoading(true);
      setProgress('Starting log upload...');
      
      Swal.fire({
        title: 'Reading Logs',
        text: 'Please wait while logs are being uploaded...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      try {
        const response = await deviceCommands.readLogs(client, user, oledDevices, (progressMsg) => {
          const content = Swal.getHtmlContainer();
          if (content) {
            const textElement = content.querySelector('.swal2-html-container');
            if (textElement) textElement.innerHTML = progressMsg;
          }
        });
        
        Swal.close();
        
        if (response?.success) {
          Swal.fire({
            title: 'Success',
            text: response.message,
            icon: 'success',
            timer: 3000,
            showConfirmButton: false
          });
          
          if (response.logs && response.logs.length > 0) {
            console.log(`Received ${response.logs.length} logs from device`);
          }
          
        } else {
          Swal.fire({
            title: 'Device offline',
            text: response?.message || 'Log read failed',
            icon: 'error'
          });
        }
      } catch (error) {
        Swal.close();
        Swal.fire({
          title: 'Device offline',
          text: error.message || 'Log read failed',
          icon: 'error'
        });
      } finally {
        setLoading(false);
        setProgress('');
      }
    }
  };
  const handleSetTime = async () => {
  const timezones = [
    'GMT',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'America/Phoenix',
    'America/Detroit',
    'America/Indianapolis'
  ];

  const { value: timezone } = await Swal.fire({
    title: 'Select Timezone',
    customClass: { popup: 'set-timezone-modal' },
    input: 'select',
    inputOptions: timezones.reduce(
      (acc, tz) => ({ ...acc, [tz]: tz }),
      {}
    ),
    inputPlaceholder: 'Select a timezone',
    showCancelButton: true
  });

  if (timezone) {
    setLoading(true);

    try {
      let value = timezone;

      // Only GMT sends current date/time
      if (timezone === 'GMT') {
        const now = new Date();

        const month = String(now.getUTCMonth() + 1).padStart(2, '0');
        const day = String(now.getUTCDate()).padStart(2, '0');
        const year = now.getUTCFullYear();
        const hours = String(now.getUTCHours()).padStart(2, '0');
        const minutes = String(now.getUTCMinutes()).padStart(2, '0');
        const seconds = String(now.getUTCSeconds()).padStart(2, '0');

        value = `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
      }

      const response = await deviceCommands.setTime(
        client,
        user,
        oledDevices,
        value
      );

      if (response?.success) {
        showAlert('Success', 'Time set successfully', true);
      } else {
        showAlert(
          'Device offline',
          response?.message || 'Failed to set time',
          false
        );
      }
    } catch (error) {
      showAlert(
        'Device offline',
        error.message || 'Failed to set time',
        false
      );
    } finally {
      setLoading(false);
    }
  }
};
  // const handleSetTime = async () => {
  //   const timezones = [
  //     'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  //     'America/Anchorage', 'America/Phoenix', 'America/Detroit', 'America/Indianapolis'
  //   ];
    
  //   const { value: timezone } = await Swal.fire({
  //     title: 'Select Timezone',
  //     customClass: { popup: 'set-timezone-modal' },
  //     input: 'select',
  //     inputOptions: timezones.reduce((acc, tz) => ({ ...acc, [tz]: tz }), {}),
  //     inputPlaceholder: 'Select a timezone',
  //     showCancelButton: true
  //   });
    
  //   if (timezone) {
  //     setLoading(true);
  //     try {
  //       const response = await deviceCommands.setTime(client, user, oledDevices, timezone);
  //       if (response?.success) {
  //         showAlert('Success', 'Time set successfully', true);
  //       } else {
  //         showAlert('Device offline', response?.message || 'Failed to set time', false);
  //       }
  //     } catch (error) {
  //       showAlert('Device offline', error.message || 'Failed to set time', false);
  //     } finally {
  //       setLoading(false);
  //     }
  //   }
  // };
  
  const handleDeviceConfig = async () => {
    // A300I uses command 79 and its own eight-value configuration format.
    if (isA300I) {
      const { value: a300Config } = await Swal.fire({
        title: 'A300 Device Settings',
        html: `
          <div class="device-config-form a300i-config-form">
            <p class="device-config-vehicle">${client.regnum || client.regNum || client.sno || client.cbid}</p>
            <div class="device-config-field a300i-toggle-field">
              <label for="a300i-status">Device Mode</label>
              <select id="a300i-status"><option value="Active">Active</option><option value="Passive">Passive</option></select>
            </div>
            <div class="device-config-field a300i-toggle-field">
              <label for="a300i-random-test">Random Test Enable</label>
              <select id="a300i-random-test"><option value="true">Enabled</option><option value="false">Disabled</option></select>
            </div>
            <div class="device-config-field"><label for="a300i-engine-off">Engine Off Timer (minutes)</label><input id="a300i-engine-off" type="number" min="0" step="any" required /></div>
            <div class="device-config-field"><label for="a300i-bac">BAC Cutoff Value</label><input id="a300i-bac" type="number" min="0" step="any" required /></div>
            <div class="device-config-field"><label for="a300i-random-interval">Random Test Interval (minutes)</label><input id="a300i-random-interval" type="number" min="0" step="any" required /></div>
            <div class="device-config-field"><label for="a300i-blow-volume">Blow Volume</label><input id="a300i-blow-volume" type="number" min="0" step="any" required /></div>
            <div class="device-config-field"><label for="a300i-blow-time">Blow Time (ms)</label><input id="a300i-blow-time" type="number" min="0" step="any" required /></div>
            <div class="device-config-field"><label for="a300i-gps-interval">GPS Interval</label><input id="a300i-gps-interval" type="number" min="0" step="any" required /></div>
          </div>`,
        customClass: { popup: 'device-config-modal a300i-config-modal' },
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
        preConfirm: () => {
          const value = id => document.getElementById(id)?.value?.trim();
          const values = [
            value('a300i-engine-off'), value('a300i-bac'), value('a300i-random-interval'),
            value('a300i-blow-volume'), value('a300i-blow-time'), value('a300i-gps-interval')
          ];
          if (values.some(item => item === '' || !Number.isFinite(Number(item)) || Number(item) < 0)) {
            Swal.showValidationMessage('Enter a valid zero or positive number for every setting.');
            return false;
          }
          return {
            status: value('a300i-status'),
            randomTestEnabled: value('a300i-random-test') === 'true',
            engineOff: values[0], bac: values[1], randomInterval: values[2],
            blowVolume: values[3], blowTime: values[4], gpsInterval: values[5]
          };
        }
      });

      if (!a300Config) return;
      const resp = [
        a300Config.engineOff,
        a300Config.bac,
        a300Config.randomTestEnabled ? '0' : '1',
        a300Config.randomInterval,
        a300Config.status === 'Active' ? a300Config.blowVolume : '0',
        a300Config.blowTime,
        a300Config.status === 'Active' ? '1' : '0',
        a300Config.gpsInterval
      ].join(',');
      await sendExtraCommand('Custom Configuration', '79', resp);
      return;
    }

    const isWisconsin = client?.tagstate === 'WI';
    const defaults = isWisconsin
      ? { cycleDays: 60, calibrationValue: 50, alcoholCutValue: 20 }
      : { cycleDays: 60, calibrationValue: 30, alcoholCutValue: 25 };

    // Older samplers only understand the original three-value configuration.
    if (firmwareVersion <= 48) {
      const result = await Swal.fire({
        title: 'Configure Device Parameters',
        text: `Apply the default configuration to ${client.regnum || client.regNum || client.sno}?`,
        icon: 'warning',
        customClass: { popup: 'device-config-modal device-config-modal-legacy' },
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) return;

      setLoading(true);
      try {
        const legacyConfig = `${defaults.cycleDays},${defaults.calibrationValue},${defaults.alcoholCutValue}`;
        const response = await deviceCommands.deviceConfig(client, user, oledDevices, legacyConfig);
        if (response?.success) {
          showAlert('Success', 'Device configured successfully', true);
        } else {
          showAlert('Device offline', response?.message || 'Configuration failed', false);
        }
      } catch (error) {
        showAlert('Device offline', error.message || 'Configuration failed', false);
      } finally {
        setLoading(false);
      }
      return;
    }

    const optionTags = (min, max, selected, step = 10) => {
      const values = [];
      for (let value = min; value <= max; value += step) values.push(value);
      if (!values.includes(selected)) values.push(selected);
      return values
        .sort((a, b) => a - b)
        .map(value => `<option value="${value}" ${value === selected ? 'selected' : ''}>${value}</option>`)
        .join('');
    };

    const field = (label, id, options) => `
      <div class="device-config-field">
        <label for="${id}">${label}</label>
        <select id="${id}">${options}</select>
      </div>`;

    const tamperingField = firmwareVersion >= 65 ? field(
      'Tampering',
      'device-config-tampering',
      '<option value="0">Disabled</option><option value="1">Enabled</option>'
    ) : '';

    let blowStrength = 'low';
    const { value: config } = await Swal.fire({
      title: 'Configure Device Parameters',
      html: `
        <div class="device-config-form">
          <p class="device-config-vehicle">${client.regnum || client.regNum || client.sno}</p>
          ${field('No. of Cycle Days', 'device-config-cycle', optionTags(10, 60, defaults.cycleDays))}
          ${field('Calibration Value', 'device-config-calibration', optionTags(10, 60, defaults.calibrationValue))}
          ${field('Alcohol Cut Value', 'device-config-alcohol', optionTags(20, 50, defaults.alcoholCutValue, 5))}
          <div class="device-config-field">
            <span>Blow Strength</span>
            <div id="device-config-strength" class="device-config-strength-grid">
              <button type="button" data-strength="low" class="device-config-strength is-selected">Low</button>
              <button type="button" data-strength="medium" class="device-config-strength">Medium</button>
              <button type="button" data-strength="high" class="device-config-strength">High</button>
            </div>
          </div>
          ${tamperingField}
        </div>`,
      icon: 'info',
      customClass: { popup: 'device-config-modal' },
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      didOpen: () => {
        const buttons = Array.from(document.querySelectorAll('.device-config-strength'));
        buttons.forEach(button => button.addEventListener('click', () => {
          blowStrength = button.dataset.strength;
          buttons.forEach(item => item.classList.toggle('is-selected', item === button));
        }));
      },
      preConfirm: () => {
        const cycleDays = document.getElementById('device-config-cycle')?.value;
        const calibrationValue = document.getElementById('device-config-calibration')?.value;
        const alcoholCutValue = document.getElementById('device-config-alcohol')?.value;
        const tampering = document.getElementById('device-config-tampering')?.value || '0';
        const strengthValues = {
          low: [35, 800],
          medium: [55, 1000],
          high: [60, 1100]
        };
        const [cutoffTime, blowVolume] = strengthValues[blowStrength];
        const values = [cycleDays, calibrationValue, alcoholCutValue, cutoffTime, blowVolume];

        // Firmware 65+ supports the additional timing and tampering parameters.
        if (firmwareVersion >= 65) values.push(30, 20, tampering);
        return values.join(',');
      }
    });

    if (!config) return;

    setLoading(true);
    try {
      const response = await deviceCommands.deviceConfig(client, user, oledDevices, firmwareVersion >= 80 ? '' : config);
      if (response?.success) {
        showAlert('Success', 'Device configured successfully', true);
      } else {
        showAlert('Device offline', response?.message || 'Configuration failed', false);
      }
    } catch (error) {
      showAlert('Device offline', error.message || 'Configuration failed', false);
    } finally {
      setLoading(false);
    }
  };
  
  const downloadInstallationLogs = (logs) => {
    const headers = ['Sampler ID', 'Control Board ID', 'Time', 'Event', 'BAC', 'Result'];
    const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csvRows = logs.map(log => [
      log.samplerId || log.devId || log.devid || '',
      log.controlId || log.cbid || '',
      log.time || log.rtime || log.updatedtime || '',
      log.event || log.typeofevent || '',
      // log.bac || log.brac || '',
      (
    String(log.event || '').toUpperCase().includes('OTP') ||
    ['41', '42', '43', '44'].includes(
      String(log.rawEventType ?? log.typeofevent ?? '').trim()
    )
  )
    ? ''
    : (log.bac || ''),

  Number(log.typeofevent ?? log.rawEventType) === 254
    ? (log.result || log.fullresp || '')
    : ''
]);
    const csv = `\uFEFF${[headers, ...csvRows]
      .map(row => row.map(escapeCsvValue).join(','))
      .join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const identifier = String(client.regNum || client.regnum || client.sno || 'device')
      .replace(/[^a-z0-9_-]/gi, '_');
    link.href = url;
    link.download = `${identifier}_logs.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const formatInstallationLogs = async (logs) => {
    let stateTimezone = '';
    try {
      const response = await fetch(
        `https://alcobrakeadmin.com/v3/us/cadevices/getuniquedevice?sno=${encodeURIComponent(client.sno || '')}`
      );
      const device = response.ok ? (await response.json())?.[0] : null;
      if (device?.timezone === 'GMT') {
        const tagState = String(device.tagstate || client.tagstate || client.tagState || '').trim().toUpperCase();
        stateTimezone = stateTimezones[tagState] || 'GMT';
      }
    } catch (error) {
      console.warn('Unable to load the device timezone for log export:', error);
    }

    return logs.map(log => formatEventLog(
      log,
      client.devicemode || client.deviceMode,
      stateTimezone
    ));
  };
const sendCertification = async (certificationType, lastValue) => {
  setLoading(true);

  try {
    const response = await deviceCommands.certification(
      client,
      user,
      oledDevices,
      certificationType,
      lastValue
    );

    if (response?.success) {
      showAlert('Success', response.message, true);
    } else {
      showAlert(
        'Device offline',
        response?.message || 'CERTIFICATION failed',
        false
      );
    }
  } catch (error) {
    showAlert(
      'Device offline',
      error.message || 'CERTIFICATION failed',
      false
    );
  } finally {
    setLoading(false);
  }
};
const sendCertificationCommand = async (certValue) => {
  try {
    // Example:
    // certValue = "50-0,0,1,3,3,3,20,5,5,5,7,45,1"

    const [command, resp] = certValue.split('-');

    const confirmSend = await Swal.fire({
      title: 'Confirm Certification',
      text: `Are you sure you want to send this certification?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Send',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280'
    });

    if (!confirmSend.isConfirmed) {
      return;
    }

    const response = await fetch(
      'https://alcobrakeadmin.com/v3/app/commands',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          devId: commandDeviceId,
          command,
          resp,
          refby: commandRefBy,
          role: user?.role
        })
      }
    );

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    await Swal.fire({
      title: 'Sent Successfully',
      text: 'Certification command sent successfully.',
      icon: 'success',
      confirmButtonColor: '#2563eb'
    });

  } catch (error) {
    console.error('Certification command error:', error);

    Swal.fire({
      title: 'Device offline',
      text: 'Failed to send certification command.',
      icon: 'error',
      confirmButtonColor: '#dc2626'
    });
  }
};

const handleCertification = async () => {
  const result = await Swal.fire({
    title: 'Certification',
    text: 'Select certification type',
    icon: 'info',
    showCancelButton: true,
    showConfirmButton: false,
    cancelButtonText: 'Cancel',
    html: `
      <div style="
        display:grid;
        grid-template-columns:1fr 1fr;
        gap:10px;
        margin-top:15px;
      ">
        <button id="cert-1" class="cert-btn cert-blue">1</button>

<button id="cert-10" class="cert-btn cert-indigo">10</button>

<button id="cert-30" class="cert-btn cert-orange">30</button>

<button id="cert-normal" class="cert-btn cert-purple">Normal</button>

<button id="cert-custom" class="cert-btn cert-dark">Custom</button>

<button id="cert-tempenable" class="cert-btn cert-green">
  Temp Enable
</button>

<button id="cert-tempdisable" class="cert-btn cert-red">
  Temp Disable
</button>

<button id="cert-temperingenable" class="cert-btn cert-amber">
  Tampering Enable
</button>

<button id="cert-temperingdisable" class="cert-btn cert-gray">
  Tampering Disable
</button>

<button id="cert-hardblowenable" class="cert-btn cert-hard-enable">
  Hard-Blow Enable
</button>

<button id="cert-hardblowdisable" class="cert-btn cert-hard-disable">
  Hard-Blow Disable
</button>

<button
  id="cert-temp-tampering-enable"
  class="cert-btn cert-teal"
>
  Temp + Tampering Enable
</button>

<button id="cert-breathalyzer" class="cert-btn cert-blue">
  LAB MODE
</button>

<button id="cert-interlock" class="cert-btn cert-indigo">
  INTERLOCK MODE
</button>

<button id="cert-device-time" class="cert-btn cert-orange">
  Set Device Time
</button>

<button id="cert-calibration-date" class="cert-btn cert-purple">
  Set Calibration Date
</button>

<button id="cert-auc-configure" class="cert-btn cert-blue">
  AUC Configure
</button>
      </div>

      <style>

      .cert-blue {
  background: #2563EB;
}

.cert-indigo {
  background: #4F46E5;
}

.cert-orange {
  background: #F97316;
}

.cert-purple {
  background: #7C3AED;
}
       .cert-btn {
  border: none;
  border-radius: 9px;
  padding: 13px 10px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cert-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.08);
}

.cert-blue {
  background: #2563EB;
}

.cert-indigo {
  background: #4F46E5;
}

.cert-orange {
  background: #F97316;
}

.cert-purple {
  background: #7C3AED;
}

.cert-dark {
  background: #374151;
}

.cert-green {
  background: #059669;
}

.cert-red {
  background: #DC2626;
}

.cert-amber {
  background: #D97706;
}

.cert-gray {
  background: #64748B;
}

.cert-hard-enable {
  background: #15803D;
}

.cert-hard-disable {
  background: #B91C1C;
}

.cert-teal {
  background: #0F766E;
}
      </style>
    `,

    didOpen: () => {

      document.getElementById('cert-1').onclick = () => {
        Swal.close({ isConfirmed: true, value: '1' });
      };

      document.getElementById('cert-10').onclick = () => {
        Swal.close({ isConfirmed: true, value: '2' });
      };

      document.getElementById('cert-30').onclick = () => {
        Swal.close({ isConfirmed: true, value: '3' });
      };

      document.getElementById('cert-normal').onclick = () => {
        Swal.close({ isConfirmed: true, value: '4' });
      };

    document.getElementById('cert-tempenable').onclick = () => { 
        Swal.close(); sendCertificationCommand( '50-0,0,1,3,3,3,20,5,5,5,7,45,1' ); 
      };
    
    document.getElementById('cert-tempdisable').onclick = () => { 
        Swal.close(); sendCertificationCommand( '50-0,0,0,3,3,3,20,5,5,5,7,45,1' ); 
      };
      
    document.getElementById('cert-temperingenable').onclick = () => { 
      Swal.close(); sendCertificationCommand( '50-1,0,0,3,3,3,20,5,5,5,7,45,1' ); 
    };
  
    document.getElementById('cert-temperingdisable').onclick = () => { 
      Swal.close(); sendCertificationCommand( '50-0,0,0,3,3,3,20,5,5,5,7,45,1' ); 
    };

    document.getElementById('cert-hardblowenable').onclick = () => {
        Swal.close();
        sendCertificationCommand('94-60,30,25,40,400,4,1,1');
      };

      document.getElementById('cert-hardblowdisable').onclick = () => {
        Swal.close();
        sendCertificationCommand('94-60,30,25,40,400,4,1,0');
      };

      document.getElementById('cert-temp-tampering-enable').onclick = () => {
        Swal.close();
        sendCertificationCommand('50-1,0,1,3,3,3,20,5,5,5,7,45,1');
      };

      document.getElementById('cert-breathalyzer').onclick = () => {
  Swal.close();
  sendCertificationCommand('51-1');
};

document.getElementById('cert-interlock').onclick = () => {
  Swal.close();
  sendCertificationCommand('51-2');
};

document.getElementById('cert-device-time').onclick = async () => {
  Swal.close();

  const result = await Swal.fire({
    title: 'Set Device Time',
    html: `
      <div style="text-align:left;">
        <label style="font-weight:600; display:block; margin-bottom:8px; text-align:center;">
          Select Date & Time
        </label>

        <input
          id="device-time-input"
          type="datetime-local"
          class="swal2-input"
          style="
  margin: 0 auto;
  display: block;
"
        />
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Send',
    cancelButtonText: 'Cancel'
  });

  if (!result.isConfirmed) return;

  const value = document.getElementById('device-time-input')?.value;

  if (!value) {
    Swal.fire('Error', 'Please select date and time', 'error');
    return;
  }

  // Selected time is browser/India local time
  const localDate = new Date(value);

  // Convert to GMT
  const month = String(localDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(localDate.getUTCDate()).padStart(2, '0');
  const year = localDate.getUTCFullYear();
  const hours = String(localDate.getUTCHours()).padStart(2, '0');
  const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(localDate.getUTCSeconds()).padStart(2, '0');

  const gmtDateTime =
    `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;

  // Directly send - NO second confirmation popup
  sendCertificationCommand(`86-${gmtDateTime}`);
};
      document.getElementById('cert-custom').onclick = async () => {
        Swal.close();

        const customResult = await Swal.fire({
          title: 'Custom Certification',
          input: 'number',
          inputLabel: 'Enter value (0-255)',
          inputPlaceholder: '0-255',
          showCancelButton: true,
          confirmButtonText: 'Send',
          cancelButtonText: 'Cancel',
          inputAttributes: {
            min: 0,
            max: 255,
            step: 1
          },
          inputValidator: (value) => {
            if (value === '') {
              return 'Please enter a value';
            }

            const num = Number(value);

            if (!Number.isInteger(num) || num < 0 || num > 255) {
              return 'Value must be between 0 and 255';
            }
          }
        });

        if (customResult.isConfirmed) {
          const confirmSend = await Swal.fire({
            title: 'Send Certification?',
            text: `Are you sure you want to send value ${customResult.value}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Send',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#2563eb'
          });

          if (confirmSend.isConfirmed) {
            sendCertification('5', customResult.value);
          }
        }
      };
document.getElementById('cert-auc-configure').addEventListener('click', async () => {

  const result = await Swal.fire({
    title: 'AUC Configure',

    html: `
      <div style="text-align:left;margin:0 20px;">

        <label style="display:block;margin-bottom:6px;">
          AUC Value
        </label>

        <input
          id="auc-value"
          type="number"
          min="5"
          max="250"
          step="any"
          placeholder="Enter AUC (5-250)"
          style="
            width:100%;
            height:42px;
            box-sizing:border-box;
            padding:0 12px;
            border:1px solid #d9d9d9;
            border-radius:5px;
            font-size:16px;
            outline:none;
          "
        >

        <label style="display:block;margin:16px 0 6px;">
          WIPER Value
        </label>

        <input
          id="adc-value"
          type="number"
          min="0"
          max="127"
          step="1"
          placeholder="Enter ADC (0-127)"
          style="
            width:100%;
            height:42px;
            box-sizing:border-box;
            padding:0 12px;
            border:1px solid #d9d9d9;
            border-radius:5px;
            font-size:16px;
            outline:none;
          "
        >

      </div>
    `,

    showCancelButton: true,
    confirmButtonText: 'Configure',
    cancelButtonText: 'Cancel',
    focusConfirm: false,

    didOpen: () => {
      document.getElementById('auc-value').focus();
    },

   preConfirm: () => {
  const aucInput = document.getElementById('auc-value');
  const adcInput = document.getElementById('adc-value');

  const aucText = aucInput.value.trim();
  const adcText = adcInput.value.trim();

  if (aucText === '' || adcText === '') {
    Swal.showValidationMessage(
      'Please enter AUC and ADC.'
    );
    return false;
  }

  // Convert values
  const aucValue = parseFloat(aucText);
  const adcValue = parseInt(adcText, 10);

  // AUC: float allowed, 5 - 250
  if (
    Number.isNaN(aucValue) ||
    aucValue < 5 ||
    aucValue > 250
  ) {
    Swal.showValidationMessage(
      `AUC must be between 5 and 250. Entered: ${aucText}`
    );
    return false;
  }

  // ADC: integer only, 0 - 127
  if (
    Number.isNaN(adcValue) ||
    adcValue < 0 ||
    adcValue > 127 ||
    String(adcValue) !== adcText
  ) {
    Swal.showValidationMessage(
      'ADC must be an integer between 0 and 127.'
    );
    return false;
  }

  return {
    auc: aucValue,
    adc: adcValue
  };
}
  });

  // User cancelled
  if (!result.isConfirmed) {
    return;
  }

  const { auc, adc } = result.value;

  try {

    const response = await deviceCommands.aucConfigure(
      client,
      user,
      oledDevices,
      auc,
      adc
    );

    if (response?.success) {

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: response.message || 'AUC configuration sent successfully'
      });

    } else {

      Swal.fire({
        icon: 'error',
        title: 'Device offline',
        text: response?.message || 'AUC configuration failed'
      });

    }

  } catch (error) {

    console.error('AUC configuration error:', error);

    Swal.fire({
      icon: 'error',
      title: 'Device offline',
      text: error?.message || 'AUC configuration failed'
    });
  }
});
      document.getElementById('cert-calibration-date').onclick = async () => {
  Swal.close();

  const result = await Swal.fire({
    title: 'Set Calibration Date',
    html: `
      <div style="text-align:left;">
        <label style="font-weight:600; display:block; margin-bottom:8px; text-align:center;">
          Select Calibration Date & Time
        </label>

        <input
          id="calibration-date-input"
          type="datetime-local"
          class="swal2-input"
          style="
  margin: 0 auto;
  display: block;
"
        />
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Send',
    cancelButtonText: 'Cancel'
  });

  if (!result.isConfirmed) return;

  const value = document.getElementById('calibration-date-input')?.value;

  if (!value) {
    Swal.fire('Error', 'Please select date and time', 'error');
    return;
  }

  // Selected time is browser/India local time
  const localDate = new Date(value);

  // Convert to GMT
  const month = String(localDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(localDate.getUTCDate()).padStart(2, '0');
  const year = localDate.getUTCFullYear();
  const hours = String(localDate.getUTCHours()).padStart(2, '0');
  const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(localDate.getUTCSeconds()).padStart(2, '0');

  const gmtDateTime =
    `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;

  // Directly send - NO second confirmation popup
  sendCertificationCommand(`105-${gmtDateTime}`);
};
      
    }
  });

  if (result?.isConfirmed) {

    const names = {
      '1': '1',
      '2': '10',
      '3': '30',
      '4': 'Normal',
      '50-0,0,1,3,3,3,20,5,5,5,7,45,1': 'Temp Enable',
      '50-0,0,0,3,3,3,20,5,5,5,7,45,1': 'Temp Disable / Tampering Disable',
      '50-1,0,0,3,3,3,20,5,5,5,7,45,1': 'Tampering Enable'
    };

    const selectedName = names[result.value] || 'Certification';

    const confirmSend = await Swal.fire({
      title: 'Confirm Certification',
      text: `Send "${selectedName}" certification?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Send',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280'
    });

    if (confirmSend.isConfirmed) {
      sendCertification(result.value, '2');
    }
  }
};
//   const handleCertification = async () => {
//   const result = await Swal.fire({
//     title: 'Certification',
//     text: 'Select certification type',
//     icon: 'question',
//     showCancelButton: true,
//     showConfirmButton: false,
//     cancelButtonText: 'Cancel',
//     html: `
//       <div style="display:flex; flex-direction:column; gap:10px;">
//         <button id="cert-1" class="swal2-confirm swal2-styled">
//           1
//         </button>

//         <button id="cert-10" class="swal2-confirm swal2-styled">
//           10
//         </button>

//         <button id="cert-30" class="swal2-confirm swal2-styled">
//           30
//         </button>

//         <button id="cert-normal" class="swal2-confirm swal2-styled">
//           Normal
//         </button>

//         <button id="cert-custom" class="swal2-confirm swal2-styled">
//           Custom
//         </button>
//       </div>
//     `,
//     didOpen: () => {
//       document.getElementById('cert-1').onclick = () => {
//         Swal.close({ isConfirmed: true, value: '1' });
//       };

//       document.getElementById('cert-10').onclick = () => {
//         Swal.close({ isConfirmed: true, value: '2' });
//       };

//       document.getElementById('cert-30').onclick = () => {
//         Swal.close({ isConfirmed: true, value: '3' });
//       };

//       document.getElementById('cert-normal').onclick = () => {
//         Swal.close({ isConfirmed: true, value: '4' });
//       };

//       document.getElementById('cert-custom').onclick = async () => {
//         Swal.close();

//         const customResult = await Swal.fire({
//           title: 'Custom Certification',
//           input: 'number',
//           inputLabel: 'Enter value (0-255)',
//           inputPlaceholder: '0-255',
//           showCancelButton: true,
//           confirmButtonText: 'Send',
//           cancelButtonText: 'Cancel',
//           inputAttributes: {
//             min: 0,
//             max: 255,
//             step: 1
//           },
//           inputValidator: (value) => {
//             if (value === '') {
//               return 'Please enter a value';
//             }

//             const num = Number(value);

//             if (!Number.isInteger(num) || num < 0 || num > 255) {
//               return 'Value must be between 0 and 255';
//             }
//           }
//         });

//         if (customResult.isConfirmed) {
//           sendCertification('5', customResult.value);
//         }
//       };
//     }
//   });

//   if (result?.isConfirmed) {
//     sendCertification(result.value, '2');
//   }
// };
  const handleNewInstallation = async () => {
    if (user.role !== 'Admin' && user.role !== 'superadmin') {
      showAlert('Access Denied', 'Only administrators can start a new installation.', false);
      return;
    }

    const credentials = await requestStorageClearCredentials(
      'New Installation',
      'OK'
    );
    if (!credentials) return;

    setLoading(true);
    setProgress('Downloading device logs...');
    Swal.fire({
      title: 'Preparing New Installation',
      text: 'Downloading logs before starting installation...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    const updateProgress = (message) => {
      setProgress(message);
      const container = Swal.getHtmlContainer();
      if (container) container.textContent = message;
    };

    try {
      const credentialResponse = await fetch(
        `https://alcobrakeadmin.com/v3/us/calogin/getuniquelogin?emailId=${encodeURIComponent(credentials.email)}&password=${encodeURIComponent(credentials.password)}`
      );
      const credentialData = await credentialResponse.json();
      if (!credentialResponse.ok || !credentialData || credentialData.length === 0) {
        throw new Error('Invalid credentials. Installation was not started.');
      }

      // Request the device to upload any missed entries first. A delayed or empty
      // upload must not prevent the existing memory-reset/configuration flow.
      const uploadResponse = await deviceCommands.readLogs(client, user, oledDevices, updateProgress);
      if (!uploadResponse?.success) {
        updateProgress('Log upload was not confirmed. Fetching Event Logs before continuing...');
      }

      // Event Logs uses this endpoint. Download whatever is already available,
      // including a single record, before the existing installation flow clears data.
      const cbid = client.cbid || client.devId;
      if (cbid) {
        try {
          const logsResponse = await fetch(
            `https://alcobrakeadmin.com/v3/v2/eeprom/getalllogs5?cbid=${encodeURIComponent(cbid)}&installdate=${encodeURIComponent(client.installDate || '')}`
          );
          const eventLogs = logsResponse.ok ? await logsResponse.json() : [];
          if (Array.isArray(eventLogs) && eventLogs.length > 0) {
            downloadInstallationLogs(await formatInstallationLogs(eventLogs));
            updateProgress(`${eventLogs.length} event log${eventLogs.length === 1 ? '' : 's'} downloaded. Continuing installation...`);
          } else {
            updateProgress('No Event Logs found. Continuing installation...');
          }
        } catch (logError) {
          console.error('Event log download failed:', logError);
          updateProgress('Could not download Event Logs. Continuing installation...');
        }
      }

      // Clear Storage (delete logs API) is disabled for New Installation.
      // Keep this block commented out so it can be restored when needed.
      // updateProgress('Clearing device storage...');
      // const clearStorageResponse = await deviceCommands.clearStorage(
      //   client,
      //   user,
      //   oledDevices,
      //   credentials.email,
      //   credentials.password
      // );
      // if (!clearStorageResponse?.success) {
      //   throw new Error(clearStorageResponse?.message || 'Unable to clear device storage. Installation was not started.');
      // }

      // Preserve the original flow: Memory Reset, Device Configuration, status
      // update, and invoice creation remain inside newInstallation.
      updateProgress('Completing new installation...');
      const response = await deviceCommands.newInstallation(client, user, oledDevices, updateProgress);
      Swal.close();
      if (response?.success) {
        showAlert('Success', 'New installation completed successfully.', true);
      } else {
        showAlert('Device offline', response?.message || 'Installation failed.', false);
      }
    } catch (error) {
      Swal.close();
      showAlert('Device offline', error.message || 'New installation failed.', false);
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  const closeDiagnostics = () => {
    diagnosticsRequestRef.current += 1;
    setDiagnosticsOpen(false);
    setDiagnosticsTab('calibration');
    setDiagnosticsLoading(false);
    setCalibrationData(null);
    setObdData(null);
  };

  const openDiagnostics = async () => {
    if (!client) return;

    const requestId = ++diagnosticsRequestRef.current;
    setDiagnosticsOpen(true);
    setDiagnosticsTab('calibration');
    setDiagnosticsLoading(true);
    setCalibrationData(null);
    setObdData(null);

    const cbid = client.cbid;
    const requests = [
      cbid
        ? fetch(`https://alcobrakeadmin.com/v3/v2/caltrack/getlatestcbidcaltrack?cbid=${encodeURIComponent(cbid)}`)
            .then(response => response.ok ? response.json() : Promise.reject(new Error('Calibration request failed')))
        : Promise.resolve([]),
      cbid
        ? fetch(`https://alcobrakeadmin.com/v3/v2/caltrack/getdevcaltracks2?cbid=${encodeURIComponent(cbid)}`)
            .then(response => response.ok ? response.json() : Promise.reject(new Error('OBD request failed')))
        : Promise.resolve([])
    ];

    const [calibrationResult, historyResult] =
      await Promise.allSettled(requests);

    if (
      requestId !== diagnosticsRequestRef.current ||
      !isMountedRef.current
    ) {
      return;
    }

    if (calibrationResult.status === 'fulfilled') {
      const data = calibrationResult.value;
      const payload = data?.data ?? data;
      const record = Array.isArray(payload) ? payload[0] : payload;
      if (isA200uDiagnostics && record) {
        const raw = record.fullresp;
        const fullResponse = typeof raw === 'string'
          ? raw
          : raw?.Valid === false ? '' : raw?.String ?? raw?.string ?? '';
        const response = String(fullResponse).trim().replace(/^NA:/i, '');
        const parameters = parseDeviceInfoResponse(response, { devicemode: 'A200U' });
        console.log('[A200U Calibration Parameters]', {
          cbid,
          updatedtime: record.updatedtime,
          fullResponse,
          receivedParameters: response ? response.split(',').length : 0,
          displayedParameters: Object.keys(parameters).length,
          parameters
        });
        setCalibrationData({ ...record, a200uParameters: parameters });
      } else {
        setCalibrationData(record || null);
      }
    } else {
      console.error('[Diagnostics] Calibration request failed', calibrationResult.reason);
    }

    if (historyResult.status === 'fulfilled') {
      const history = Array.isArray(historyResult.value)
        ? [...historyResult.value].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
        : [];
      const obdRecord = history.find(record => parseObdRecord(record));
      setObdData(obdRecord ? parseObdRecord(obdRecord) : null);
    }

    setDiagnosticsLoading(false);
  };

  const isA200uDiagnostics = String(
    client?.devicemode || client?.deviceMode || client?.devicemodel || ''
  ).trim().toUpperCase() === 'A200U';

  const calibrationGroups = buildCalibrationGroups(calibrationData, isA200uDiagnostics);

  const obdGroups = OBD_GROUPS.map(group => ({
    ...group,
    items: group.parameters
      .map(parameter => obdData?.rows.find(row => row.parameter === parameter))
      .filter(Boolean)
  }));

  const deviceInfoGroups = deviceInfoCommandData
    ? (deviceInfoCommandMode === 'A200U'
      ? (() => {
          const knownParameters = new Set(
            [
              ...A200U_DEVICE_INFO_GROUPS.flatMap(group => group.parameters),
              ...HIDDEN_A200U_DEVICE_INFO_PARAMETERS
            ]
          );
          const groups = A200U_DEVICE_INFO_GROUPS.map(group => ({
            ...group,
            items: group.parameters
              // .filter(parameter => Object.prototype.hasOwnProperty.call(deviceInfoCommandData, parameter))
              .filter(parameter =>
  !HIDDEN_A200U_DEVICE_INFO_PARAMETERS.has(parameter) &&
  Object.prototype.hasOwnProperty.call(deviceInfoCommandData, parameter)
)
             .map(parameter => ({
            parameter,
            value: formatDiagnosticParameter(parameter, deviceInfoCommandData[parameter]),
            icon: getDeviceInfoPresentation(parameter).icon
          }))
          })).filter(group => group.items.length);
          const additionalItems = Object.entries(deviceInfoCommandData)
            .filter(([parameter]) => !knownParameters.has(parameter))
            .map(([parameter, value]) => ({
              parameter,
              value: formatDiagnosticParameter(parameter, value),
              icon: getDeviceInfoPresentation(parameter).icon
            }));

          if (additionalItems.length) {
            groups.push({
              title: 'Additional Device Parameters',
              icon: Database,
              items: additionalItems
            });
          }

          return groups;
        })()
      : buildDynamicDeviceInfoGroups(deviceInfoCommandData))
    : [];

  const closeDeviceInfoModal = () => {
    setDeviceInfoModalOpen(false);
    setDeviceInfoCommandData(null);
    setDeviceInfoCommandMode('');
  };
  
  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  };
  
  const avatarColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#06b6d4'];
  const getAvatarColor = (name) => avatarColors[name?.charCodeAt(0) % avatarColors.length] || '#3b82f6';
  
  // ✅ Use numeric firmware version for command availability
  // Correct comparison: numericFirmware > 0.94
  const hasReadLogsCommand = firmwareVersion > 0.94;
  const hasRestartCommand = firmwareVersion;
  
  if (!isOpen || !client) return null;
  
  return (
    <>
      {showSwap && onAddToRemovals && (
        <DeviceSwapModal key={client.id} device={{ ...client, fullclientid: client.id }} user={user}
          onRemove={replacementClient => onAddToRemovals(replacementClient)}
          onClose={() => setShowSwap(false)}
          onSuccess={() => {
            setShowSwap(false);
            onClose();
            onSwapSuccess?.();
            Swal.fire("Device replaced", "Device replaced successfully.", "success");
          }} />
      )}
      {!!otpNotice && !otpLoading && (
        <div className="client-otp-alert" role="alert">{otpNotice}</div>
      )}
      <div className="drawer-overlay" onClick={onClose}></div>
      <div className="drawer">
        <div className="drawer-header">
          <div className="drawer-title">
            <div className="drawer-avatar" style={{ background: getAvatarColor(client.name) }}>
              {getInitials(client.name)}
            </div>
            <div>
              <h2>{client.name}</h2>
              <p>{client.sno} • {client.regNum}</p>
            </div>
          </div>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="drawer-body">
                {onAddToRemovals && (
                  <button type="button" className="device-swap-button" style={{ marginBottom: 12 }}
                    disabled={loading || !client.cbid || !client.id} onClick={() => {
                      console.log('[Device swap] Swap control board clicked', { cbid: client.cbid, fullclientid: client.id });
                      setShowSwap(true);
                    }}>
                    Replace Device
                  </button>
                )}
          {loading && progress && (
            <div className="progress-indicator">
              <div className="loader-small"></div>
              <span>{progress}</span>
            </div>
          )}
          
          {/* Show loading skeleton while data is being fetched */}
          {isDataLoading ? (
            <div className="device-info-loading">
              <div className="section-title">Device Information</div>

              <div className="info-grid-loading">
                <div className="skeleton-row"></div>
                <div className="skeleton-row"></div>
                <div className="skeleton-row"></div>
                <div className="skeleton-row"></div>
                <div className="skeleton-row"></div>
                <div className="skeleton-row"></div>
              </div>
              <div className="commands-section">
                <div className="section-title">Device Commands</div>
                <div className="commands-grid-loading">
                  <div className="skeleton-btn"></div>
                  <div className="skeleton-btn"></div>
                  <div className="skeleton-btn"></div>
                  <div className="skeleton-btn"></div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="connection-status">
                <div className={`signal-icon ${connectionStatus.cellular === 'on' ? 'active live' : connectionStatus.cellular === 'checking' ? 'checking' : 'inactive'}`}>
                  <span className="signal-text">4G</span>
                  {connectionStatus.cellular === 'on' ? '📶' : connectionStatus.cellular === 'checking' ? '⏳' : '📡❌'}
                </div>
                
                <div className={`wifi-icon ${connectionStatus.wifi === 'on' ? 'active live' : connectionStatus.wifi === 'checking' ? 'checking' : 'inactive'}`}>
                  <span className="wifi-text">WiFi</span>
                  {connectionStatus.wifi === 'on' ? '📶' : connectionStatus.wifi === 'checking' ? '⏳' : '📡❌'}
                </div>
              </div>
              
              <div className="device-info-section">
                <div className="section-title">Device Information</div>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Device ID:</span>
                    <span className="info-value mono">{deviceInfo.samplerId}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">HandSet:</span>
                    <span className="info-value mono">{deviceInfo.controlBoardId}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Sampler Board Version:</span>
                    <span className="info-value">
                      {deviceInfo.firmware === 'Loading...' ? (
                        <span className="loading-text">Loading...</span>
                      ) : (
                        deviceInfo.firmware
                      )}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Control Board Version:</span>
                    <span className="info-value">
                      {deviceInfo.cbVersion === 'Loading...' ? (
                        <span className="loading-text">Loading...</span>
                      ) : (
                        deviceInfo.cbVersion
                      )}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">S3 Version:</span>
                    <span className="info-value">{deviceInfo.s3Version || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Calibration Date:</span>
                    <span className="info-value">{deviceInfo.calibrationDate}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Lockout Date:</span>
                    <span className="info-value">{deviceInfo.lockoutDate}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Update:</span>
                    <span className="info-value">{deviceInfo.lastUpdate}</span>
                  </div>
                </div>
              </div>
              
              <div className="commands-section">
                <div className="commands-header">
                  <div className="section-title">Device Commands</div>
                  <button className="cmd-btn diagnostics diagnostics-header-btn" onClick={openDiagnostics} disabled={loading}>
                    <Stethoscope size={16} aria-hidden="true" /> Diagnostics 
                  </button>
                </div>
                {/* <div role="status" style={{ marginBottom: 12 }}>
                  {!mqttDeviceId ? 'No device ID available.' : mqttStatus === 'connected'
                    ? 'MQTT connected. Commands use MQTT.'
                    : mqttStatus === 'checking' ? 'Connecting to MQTT (up to 30 seconds). Commands currently use the API.'
                    : 'MQTT unavailable. Commands use the API.'}
                  {mqttDeviceId && ['failed', 'disconnected'].includes(mqttStatus) && (
                    <button type="button" onClick={() => setMqttAttempt(attempt => attempt + 1)}>Retry connection</button>
                  )}
                </div> */}
         {/* <div role="status" style={{ marginBottom: 12 }}>
  {!mqttDeviceId
    ? 'No device ID available.'
    : mqttStatus === 'connected'
    ? 'MQTT connected.'
    : 'Normal API used.'}

  {mqttDeviceId &&
    ['failed', 'disconnected'].includes(mqttStatus) && (
      <button
        type="button"
        onClick={() =>
          setMqttAttempt(attempt => attempt + 1)
        }>
        Retry connection
      </button>
    )}
</div> */}
                <div className="commands-grid">
                  <button className="cmd-btn blue" onClick={handleResetViolation} disabled={loading}>
                    🔄 Reset Violation <CommandNetworkIcons />
                  </button>
                  
                  <button className="cmd-btn green" onClick={handleTakeTest} disabled={loading}>
                    ✓ Take Test <CommandNetworkIcons />
                  </button>
                  
                  <button className="cmd-btn orange" onClick={handleDeviceConfig} disabled={loading}>
                    ⚙️ Device Config <CommandNetworkIcons />
                  </button>
                  
                  {isA300U && (
                    <button className="cmd-btn state-config" onClick={handleStateRequirements} disabled={loading}>
                      <Radio size={15} aria-hidden="true" />
                      State Requirements <CommandNetworkIcons />
                    </button>
                  )}

                  {isA300I && (
                    <>
                      <button className="cmd-btn custom-config" onClick={handleDeviceConfig} disabled={loading}>
                        <Radio size={15} aria-hidden="true" />
                        Custom Config <CommandNetworkIcons />
                      </button>
                      <button className="cmd-btn admin-settings" onClick={handleAdminSettings} disabled={loading}>
                        <Radio size={15} aria-hidden="true" />
                        Admin Settings <CommandNetworkIcons />
                      </button>
                    </>
                  )}

                  {isA300U && (
                    <>
                      <button
                        className="cmd-btn bypass-time"
                        onClick={() => promptNumberCommand({
                          title: 'Bypass Timeset',
                          label: 'Bypass time',
                          command: '40',
                          min: 1,
                          max: 600
                        })}
                        disabled={loading}
                      >
                        Bypass Timeset <CommandNetworkIcons />
                      </button>
                      <button
                        className="cmd-btn bypass-day"
                        onClick={() => promptNumberCommand({
                          title: 'Bypass Timeset Day',
                          label: 'Bypass day value',
                          command: '30',
                          min: 1,
                          max: 600
                        })}
                        disabled={loading}
                      >
                        Bypass Timeset Day <CommandNetworkIcons />
                      </button>
                      <button
                        className="cmd-btn multidays-config"
                        onClick={() => promptEnableValueCommand({
                          title: 'MultiDays Config',
                          valueLabel: 'Enter days',
                          command: '20',
                          note: 'When enabled, the device will lock for the specified days after a violation.'
                        })}
                        disabled={loading}
                      >
                        <Radio size={15} aria-hidden="true" />
                        MultiDays Config <CommandNetworkIcons />
                      </button>
                      <button
                        className="cmd-btn violation-count"
                        onClick={() => promptNumberCommand({
                          title: 'Violation Count',
                          label: 'Enter violation count',
                          command: '21',
                          min: 1,
                          note: 'The device will be restricted once this count is reached.'
                        })}
                        disabled={loading}
                      >
                        <Radio size={15} aria-hidden="true" />
                        Violation Count <CommandNetworkIcons />
                      </button>
                      <button
                        className="cmd-btn violation-enable"
                        onClick={() => promptEnableOnlyCommand({
                          title: 'Violation Enable',
                          command: '22',
                          note: 'Enable or disable violation count feature.'
                        })}
                        disabled={loading}
                      >
                        <Radio size={15} aria-hidden="true" />
                        Violation Enable <CommandNetworkIcons />
                      </button>
                    </>
                  )}

                  <button className="cmd-btn teal" onClick={handleSetTime} disabled={loading}>
                    ⏰ Set Time <CommandNetworkIcons />
                  </button>
                  
                  <button className="cmd-btn indigo" onClick={handleSetOTP} disabled={loading}>
                    📡 Set OTP <CommandNetworkIcons />
                  </button>
                  
                  <button 
                    className={`cmd-btn pink ${hasReadLogsCommand ? '' : 'disabled'}`} 
                    onClick={handleReadLogs} 
                    disabled={loading || !hasReadLogsCommand}
                  >
                    📊 Read Logs <CommandNetworkIcons />
                  </button>
                  
                  <button className="cmd-btn gray" onClick={handleDeviceInfo} disabled={loading}>
                    ℹ️ Device Info <CommandNetworkIcons />
                  </button>

                  {/* {deviceMode === 'A200U' && (
                    <button className="cmd-btn teal" onClick={handleDeviceInfoPreview} disabled={loading}>
                      Preview A200U Info
                    </button>
                  )} */}
                  
                  <button className="cmd-btn brown" onClick={handleTakePicture} disabled={loading}>
                    📸 Take Picture <CommandNetworkIcons />
                  </button>
                  
                  <button className="cmd-btn warning" onClick={handleBypass} disabled={loading}>
                    🚫 Bypass <CommandNetworkIcons />
                  </button>
                  
                  <button className="cmd-btn warning" onClick={handleUnbypass} disabled={loading}>
                    🔓 Unbypass <CommandNetworkIcons />
                  </button>
                  
                  <button className="cmd-btn purple" onClick={handleLockDevice} disabled={loading}>
                    🔒 Lock Device <CommandNetworkIcons />
                  </button>

                    <button className="cmd-btn cyan" onClick={handleUnlockDevice} disabled={loading}>
                    🔓 Unlock Device <CommandNetworkIcons />
                  </button>

                  <button className="cmd-btn" onClick={handleWifiChange} disabled={loading}  style={{
                      background: '#ecfdf5',
                      color: '#047857',
                      border: '1px solid #6ee7b7'
                    }}>
                    📡 Wifi Configure <CommandNetworkIcons />
                  </button>

                   <button
                    className="cmd-btn"
                    onClick={handleGpsChange}
                    disabled={loading}
                    style={{
                      background: '#ecfdf5',
                      color: '#047857',
                      border: '1px solid #6ee7b7'
                    }}
                  >
                    ⏱️ GPS Interval Configure <CommandNetworkIcons />
                  </button>

                 <button className="cmd-btn" onClick={handleInternalInfo} disabled={loading}
                     style={{
                    background: '#e0f2fe',
                    color: '#0369a1',
                    border: '1px solid #7dd3fc'
                  }}>
                     ℹ️ Internal Info <CommandNetworkIcons />
                  </button>
                  
                
                  
                  {Boolean(hasRestartCommand) && (
                    <button className="cmd-btn green" onClick={handleRestartDevice} disabled={loading}>
                      🔄 Restart Device <CommandNetworkIcons />
                    </button>
                  )}
                  
                  {(user.role === 'Admin' || user.role === 'superadmin') && (
                    <>
                      <button className="cmd-btn red" onClick={handleClearStorage} disabled={loading}>
                        🗑️ Clear Device Storage
                      </button>
                      
                      <button className="cmd-btn red" onClick={handleMemoryReset} disabled={loading}>
                        🗑️ Memory Reset <CommandNetworkIcons />
                      </button>
                      
                      <button className="cmd-btn post-command-grid-btn" onClick={openPostCommands} disabled={loading || !client.cbid}>
                        <Plus size={16} aria-hidden="true" /> Post Commands
                      </button>

                      <button className="cmd-btn green" onClick={handleNewInstallation} disabled={loading}>
                        🆕 New Installation <CommandNetworkIcons />
                      </button>

                      <button
  className="cmd-btn purple"
  onClick={handleCertification}
  disabled={loading}
>
  📜 Certification <CommandNetworkIcons />
</button>
                    </>
                  )}
                </div>
              </div>
              
              {/* OTP Section */}
              <div className="otp-section">
                <div className="otp-header">
                  <div className="section-title">Emergency OTPs</div>
                  <button className="otp-refresh-btn" onClick={fetchOTPs} disabled={otpLoading}>
                    🔄 Refresh
                  </button>
                </div>
                
                {otpLoading ? (
                  <div className="otp-loading">Loading OTPs...</div>
                ) : otpError ? (
                  <div className="otp-loading otp-error">{otpError}</div>
                ) : (
                  <div className="otp-grid">
                    {positions.map((position) => (
                      <div 
                        key={position} 
                        className={`otp-card ${getOTPStatus(position).className} ${String(position) === '4' ? 'otp-admin' : ''}`}
                      >
                        <div className="otp-position">{String(position) === '4' ? 'Admin OTP' : `Emergency OTP ${Number(position) + 1}`}</div>
                        <div className="otp-code">
                          {String(getOTPForPosition(position)).padStart(4, '0')}
                        </div>
                        <div className={`otp-status ${getOTPStatus(position).className}`}>{getOTPStatus(position).label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </>
          )}
        </div>
      </div>

      {diagnosticsOpen && (
        <div className="diagnostics-modal-overlay" onMouseDown={closeDiagnostics}>
          <section
            className="diagnostics-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="diagnostics-title"
            onMouseDown={event => event.stopPropagation()}
          >
            <header className="diagnostics-modal-header">
              <div>
                <h2 id="diagnostics-title">Diagnostics</h2>
                <span>{client.regNum || client.regnum || client.sno}</span>
                <span style={{ display: 'block', marginTop: 4 }}>
                  S.No.: {client.sno || 'N/A'} · Updated: {diagnosticsLoading
                    ? 'Loading...'
                    : getCalibrationValue(calibrationData, ['updatedtime']).replace(' (IST)', '')}
                </span>
              </div>
              <button
                type="button"
                className="diagnostics-close"
                onClick={closeDiagnostics}
                aria-label="Close diagnostics"
              >
                <X size={20} />
              </button>
            </header>

            <nav className="diagnostics-tabs" aria-label="Diagnostics views">
              {[
                ['calibration', 'Calibration Parameters'],
                ['obd', 'OBD Parameters'],
                ['operations', 'Last Operation']
              ].map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  className={diagnosticsTab === tab ? 'active' : ''}
                  onClick={() => setDiagnosticsTab(tab)}
                >
                  {label}
                </button>
              ))}
            </nav>

            <div className="diagnostics-modal-body">
              {diagnosticsLoading && (
                <div className="diagnostics-loading">
                  <LoaderCircle size={34} />
                  <span>Loading diagnostics...</span>
                </div>
              )}

              {!diagnosticsLoading && diagnosticsTab === 'calibration' && (
                calibrationData ? (
                  <div className="diagnostics-sections">
                    {calibrationGroups.map(group => {
                      const GroupIcon = group.icon;
                      return (
                        <section className="diagnostics-section" key={group.title}>
                          <div className="diagnostics-section-header">
                            <GroupIcon size={17} />
                            <span>{group.title}</span>
                          </div>
                          <div className="diagnostics-card-grid">
                            {group.items.map(item => {
                              const ItemIcon = item.icon;
                              return (
                                <div
                                  className={`diagnostics-data-card ${item.inline ? 'inline-value' : ''}`}
                                  key={item.label}
                                >
                                  <span className="diagnostics-card-icon">
                                    <ItemIcon size={21} />
                                  </span>
                                  <span className="diagnostics-card-content">
                                    <span className="diagnostics-card-label">{item.label}</span>
                                    <strong>{item.value}</strong>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                ) : (
                  <div className="diagnostics-empty">
                    No calibration parameters available
                  </div>
                )
              )}

              {!diagnosticsLoading && diagnosticsTab === 'obd' && (
                obdData ? (
                  <div className="diagnostics-sections">
                    <div className="obd-diagnostics-title">
                      <div>Vehicle OBD Parameters</div>
                      <span>Updated: {formatObdUpdatedTime(obdData.updatedtime)}</span>
                    </div>
                    {obdGroups.map(group => {
                      const GroupIcon = group.icon;
                      return (
                        <section className="diagnostics-section" key={group.title}>
                          <div className="diagnostics-section-header">
                            <GroupIcon size={17} />
                            <span>{group.title}</span>
                          </div>
                          <div className="diagnostics-card-grid">
                            {group.items.map(item => {
                              const ItemIcon = item.icon;
                              return (
                                <div className="diagnostics-data-card" key={item.parameter}>
                                  <span className="diagnostics-card-icon">
                                    <ItemIcon size={21} />
                                  </span>
                                  <span className="diagnostics-card-content">
                                    <span className="diagnostics-card-label">{item.parameter}</span>
                                    <strong>{item.value}</strong>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                ) : (
                  <div className="diagnostics-empty">
                    No Vehicle OBD Parameters available
                  </div>
                )
              )}

              {!diagnosticsLoading && diagnosticsTab === 'operations' && (
                <LastOperationProgress
                  client={client}
                  firmwareVersion={firmwareVersion}
                />
              )}
            </div>
          </section>
        </div>
      )}

      {deviceInfoModalOpen && (
        <div className="diagnostics-modal-overlay" onMouseDown={closeDeviceInfoModal}>
          <section
            className="diagnostics-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="device-info-command-title"
            onMouseDown={event => event.stopPropagation()}
          >
            <header className="diagnostics-modal-header">
              <div>
                <h2 id="device-info-command-title">Device Information</h2>
                <span>
                  {deviceInfoCommandMode || 'Unknown device'} · {Object.keys(deviceInfoCommandData || {}).length} parameters
                  {' · Updated: '}
                  {String(
                    deviceInfoCommandData?.cam1_time ||
                    deviceInfoCommandData?.deviceTime ||
                    client?.updatedtime || '—'
                  ).replace(/\s*\(IST\)/i, '')}
                </span>
              </div>
              <button
                type="button"
                className="diagnostics-close"
                onClick={closeDeviceInfoModal}
                aria-label="Close device information"
              >
                <X size={20} />
              </button>
            </header>

            <div className="diagnostics-modal-body">
              <div className="diagnostics-sections">
                {deviceInfoGroups.map(group => {
                  const GroupIcon = group.icon;
                  return (
                    <section className="diagnostics-section" key={group.title}>
                      <div className="diagnostics-section-header">
                        <GroupIcon size={17} />
                        <span>{group.title}</span>
                      </div>
                      <div className="diagnostics-card-grid">
                        {group.items.map(item => (
                          <div className="diagnostics-data-card" key={item.parameter}>
                            <span className="diagnostics-card-icon">
                              {React.createElement(item.icon || Cpu, { size: 21, 'aria-hidden': true })}
                            </span>
                            <span className="diagnostics-card-content">
                              <span className="diagnostics-card-label">
                                {formatDeviceInfoLabel(item.parameter)}
                              </span>
                              <strong>{item.value === '' ? '—' : String(item.value)}</strong>
                            </span>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      )}

      {postCommandOpen && (
        <div className="post-command-overlay" onMouseDown={closePostCommand}>
          <form className="post-command-modal post-command-queue-modal" onSubmit={submitPostCommand} onMouseDown={event => event.stopPropagation()}>
            <header className="post-command-modal-header">
              <div><h2>{postCommandView === 'list' ? 'Queue Commands' : 'Post Command'}</h2><span>{client.name || client.cbid}</span></div>
              <button type="button" className="post-command-close" onClick={closePostCommand} aria-label="Close"><X size={20} /></button>
            </header>
            {postCommandView === 'list' ? <div className="post-command-list-body">
              <div className="post-command-toolbar"><span>{postCommands.length} command{postCommands.length === 1 ? '' : 's'}</span><div><button type="button" className="post-command-cancel" onClick={fetchPostCommands} disabled={postCommandsLoading}>Refresh</button><button type="button" className="post-command-submit" onClick={() => setPostCommandView('add')}><Plus size={16} /> Add Command</button></div></div>
              {postCommandsLoading ? <div className="post-command-state"><LoaderCircle size={26} className="spin" /> Loading commands...</div>
                : postCommandsError ? <div className="post-command-state error">{postCommandsError}<button type="button" onClick={fetchPostCommands}>Try again</button></div>
                : postCommands.length === 0 ? <div className="post-command-state">No queued commands found.</div>
                : <><div className="post-command-table-wrap"><table className="post-command-table"><thead><tr><th>Device ID</th><th>Action</th><th>Grace Time</th><th>Operation Date</th><th>Status</th><th>Actions</th></tr></thead><tbody>
                  {postCommands.slice((postCommandsPage - 1) * 5, postCommandsPage * 5).map((item, index) => { 
                    // const option = postCommandOptions.find(([value]) => value === String(item.command));
                    const option = postCommandOptions.find(([value]) =>
  value === String(item.command === '95' ? '94' : item.command)
);
                    return <tr key={item.id || `${item.command}-${index}`}><td>{item.deviceid || '—'}</td><td><strong>{option?.[1] || 'Unknown'}</strong><small>{item.command}</small></td><td>{item.gracetime || '00:00:00'}</td><td>{item.operationdate || '—'}</td><td><span className={`post-command-status ${String(item.status || '').toLowerCase()}`}>{item.status || '—'}</span></td>
                  <td className="post-command-actions">
                  <button className="post-command-edit-btn" onClick={() => editPostCommand(item)}>
                    Edit
                  </button>

                  <button className="post-command-delete-btn" onClick={() => deletePostCommand(item.id)}>
                    Delete
                  </button>
                </td>
                  </tr>; })}
                </tbody></table></div>{postCommands.length > 5 && <div className="post-command-pagination"><button type="button" disabled={postCommandsPage === 1} onClick={() => setPostCommandsPage(page => page - 1)}>Previous</button><span>{postCommandsPage} / {Math.ceil(postCommands.length / 5)}</span><button type="button" disabled={postCommandsPage === Math.ceil(postCommands.length / 5)} onClick={() => setPostCommandsPage(page => page + 1)}>Next</button></div>}</>}
            </div> : <><div className="post-command-modal-body">
              <label className="post-command-field">
                <span>Operation</span>
                <select required value={postCommandForm.command} onChange={event => setPostCommandForm(form => ({ ...form, command: event.target.value }))}>
                  <option value="">Select operation</option>
                  {postCommandOptions.map(([value, label]) => <option key={value} value={value}>{label} ({value})</option>)}
                </select>
              </label>
              <label className="post-command-field">
                <span>Operation date</span>
                 <input
                  required
                  type="datetime-local"
                  value={postCommandForm.operationDate}
                  onChange={event =>
                    setPostCommandForm(form => ({
                      ...form,
                      operationDate: event.target.value
                    }))
                  }
                />
                {/* <input required type="datetime-local" min={timezoneCacheRef.current[client.cbid] ? moment().tz(timezoneCacheRef.current[client.cbid]).format('YYYY-MM-DDTHH:mm') : undefined} value={postCommandForm.operationDate} onChange={event => setPostCommandForm(form => ({ ...form, operationDate: event.target.value }))} /> */}
              </label>
              <label className="post-command-field">
                <span>Grace time</span>
                <input type="text" value={postCommandForm.graceTime} onChange={event => setPostCommandForm(form => ({ ...form, graceTime: event.target.value }))} />
                <small>Optional; defaults to 00:00:00.</small>
              </label>
              <label className="post-command-field">
                <span>Status</span>
                <select value={postCommandForm.status} onChange={event => setPostCommandForm(form => ({ ...form, status: event.target.value }))}>
                  {['Active', 'Inactive', 'Timeout', 'Executed'].map(value => <option key={value}>{value}</option>)}
                </select>
              </label>
            </div>
            <footer className="post-command-modal-footer">
              <button type="button" className="post-command-cancel" onClick={() => setPostCommandView('list')}>Back</button>
              <button type="submit" className="post-command-submit" disabled={postCommandSaving}>
                {postCommandSaving ? <><LoaderCircle size={16} className="spin" /> Posting...</> : 'Post Command'}
              </button>
            </footer>
            </>}
          </form>
        </div>
      )}
    </>
  );
};

export default ClientDetailsDrawer;
